import { useState, useEffect, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import SelectField from '../common/SelectField.js';
import { shipmentService } from "../../services/shipmentService"
import shipmentClientService from "../../services/ShipmentClientService.js"
import companySettingsService from "../../services/companySettingsService.js"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Alert from "../common/Alert"
import { MapPin, ArrowLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Plus, Send } from "lucide-react"
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import Button from '../common/Button.js'
import { isExternalNetworkCompany, mergeCanAccess } from '../../utils/companyFeatures.js'
import { hasShipmentCrossingAccess } from '../../utils/networkMode.js'
import {
  billingPartyGst,
  gstinIsInvalid,
  gstinIsRegistered,
  gtaGstRates,
  senderPaysMode,
} from '../../utils/gstRegistration.js'
import { getLocalDateString } from '../../utils/localDate.js'
import {
  formatHubBranchLabel,
  getGcRoutePrefix,
  getGcPaymentCode,
  getGcYearSuffix,
  filterShipmentLocations,
  isAllowedBookingDestination,
} from '../../utils/destinationType.js'
import {
  getSuggestionListClass,
  getSuggestionItemClass,
  handleSuggestionDropdownKeyDown,
  scrollHighlightedSuggestionIntoView,
} from '../../utils/suggestionDropdown.js'
import { useTranslation } from 'react-i18next';
import { PackageDimensionForm } from './package-dimension/index.js';
import {
  emptyBusArticle,
  sumChargeableWeight,
  sumParcelQty,
  sumBusArticleWeight,
  firstBusArticleWithoutCharge,
  calcFreightFromRate,
  calcBusFreight,
  parcelsForApi,
  resolveCargoDefaults,
  calcInsuranceFromSettings,
  syncFormFromBusArticles,
  toNum,
  recomputeParcel,
  defaultParcelsForMode,
  parcelsMatchingMode,
  parcelsAreGstExempt,
  isFixedPerParcelCharge,
  chargeAmountForSubmit,
  scaledPerParcelCharge,
} from '../../utils/cargoParcelUtils.js';
import BusArticlePackagesForm from './BusArticlePackagesForm.js';
import { getVehicleCapacityKg, formatKg } from '../../utils/vehicleLoad.js';
import { mlSheetService } from '../../services/mlSheetService';
import { hasInternalHubDispatchControls, isPickupMan } from '../../utils/networkMode.js';
import PaymentQrModal from '../common/PaymentQrModal.js';
import ShipmentMediaUploader from './ShipmentMediaUploader.js';
import GstVerificationField from './GstVerificationField.js';
import ShipmentCreatePreviewModal from './ShipmentCreatePreviewModal.js';
import PaymentStatusToggle from './PaymentStatusToggle.js';

/** Optional Rate/KG from a location hub (receiver). Empty/missing → no override. */
const rateFromHub = (hub) => {
  if (!hub || hub.rate_per_kg === null || hub.rate_per_kg === undefined || hub.rate_per_kg === '') {
    return null
  }
  const n = Number(hub.rate_per_kg)
  if (!Number.isFinite(n) || n < 0) return null
  return String(n)
}

const resolveHubIdFromLocation = (locationName, hubId, hubs) => {
  if (hubId !== null && hubId !== undefined && hubId !== "") return hubId
  if (!locationName || !Array.isArray(hubs) || hubs.length === 0) return ""
  const normalized = locationName.trim().toLowerCase()
  const match = hubs.find((h) => {
    const label = formatHubBranchLabel(h).toLowerCase()
    const loc = (h.location || "").trim().toLowerCase()
    return label === normalized || loc === normalized
  })
  return match?.id ?? ""
}

const getLocationHubPrimaryId = (loc) => loc?.id ?? ""

const formatLocationDropdownLabel = (loc) => {
  const name = formatHubBranchLabel(loc)
  const code = (loc?.location_code || loc?.hub_code || "").trim()
  return code ? `${name} (${code})` : name
}

/** Closed From/To field: name (code) from hub id; fallback to stored name-only label. */
const getSelectedLocationDisplay = (hubId, locations, fallbackLabel) => {
  if (hubId !== null && hubId !== undefined && hubId !== "") {
    const match = Array.isArray(locations)
      ? locations.find((h) => String(h.id) === String(hubId))
      : null
    if (match) return formatLocationDropdownLabel(match)
  }
  return fallbackLabel || ""
}

const paymentOptionsForHub = (hub) => [
  hub?.allow_to_pay !== false && { value: 'to_pay', label: 'To Pay' },
  hub?.allow_paid !== false && { value: 'paid', label: 'Paid' },
  hub?.allow_on_account !== false && { value: 'credit', label: 'Credit' },
].filter(Boolean)

const MOBILE_STEPS = [
  { id: "location", label: "Location" },
  { id: "consignor", label: "Consignor" },
  { id: "consignee", label: "Consignee" },
  { id: "details", label: "Shipment" },
  { id: "payment", label: "Payment" },
]

const EWAY_BILL_MANDATORY_VALUE = 50000

const applyEwayFieldsToForm = (prev, fields = {}) => {
  const next = { ...prev }
  const setIf = (key, value) => {
    if (value === undefined || value === null) return
    const text = String(value).trim()
    if (!text) return
    next[key] = text
  }
  setIf("sender_name", fields.sender_name)
  setIf("sender_gst", fields.sender_gst)
  setIf("sender_address", fields.sender_address)
  setIf("receiver_name", fields.receiver_name)
  setIf("receiver_gst", fields.receiver_gst)
  setIf("receiver_address", fields.receiver_address)
  setIf("parcel_detail", fields.parcel_detail)
  if (fields.shipment_value !== undefined && fields.shipment_value !== null && fields.shipment_value !== "") {
    const numeric = Number(fields.shipment_value)
    if (!Number.isNaN(numeric)) {
      next.shipment_value = String(numeric)
    }
  }
  return next
}

const AddShipment = () => {
  const { t } = useTranslation();
  const [submittedEwayBills, setSubmittedEwayBills] = useState([])
  const senderRef = useRef(null)
  const receiverRef = useRef(null)
  const mlSheetDropdownRef = useRef(null)
  const agentDropdownRef = useRef(null)
  const vehicleDropdownRef = useRef(null)
  const paidPaymentFieldsRef = useRef(null)
  const parcelImagesRef = useRef(null)
  const prevModeOfPaymentRef = useRef("to_pay")
  const [showEwayBillModal, setShowEwayBillModal] = useState(false)
  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false)
  const [showParcelImages, setShowParcelImages] = useState(false)
  const [showGSTDetails, setShowGSTDetails] = useState(false)
  const [ewayBills, setEwayBills] = useState([""])
  const [ewayLookupLoading, setEwayLookupLoading] = useState(false)
  const isValidEway = (value) => /^\d{12}$/.test(value)
  const handleCloseEwayBillModal = () => {
    setShowEwayBillModal(false)
    setEwayBills([""]) // reset popup state
  }
  const navigate = useNavigate()
  const { user } = useAuth()
  const canEditAdditionalCharges = ["Admin", "System Admin"].includes(user?.role || user?.role_name)
  const AGENT_UNLOCKED_CHARGE_FIELDS = ["door_pickup_charges", "delivery_charges", "other_charges"]
  const canEditChargeField = (name) =>
    canEditAdditionalCharges || AGENT_UNLOCKED_CHARGE_FIELDS.includes(name)
  const ADDITIONAL_CHARGE_FIELDS = [
    "insurance_charges", "loading_charges", "unloading_charges", "toll_charges",
    "fuel_surcharge", "packing_charges", "handling_charges", "docket_fee",
    "late_charges", "door_pickup_charges", "delivery_charges", "other_charges",
  ]
  const chargeFieldClass = (name) =>
    `px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${canEditChargeField(name) ? "bg-white" : "bg-gray-100 cursor-not-allowed"}`
  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {})
  const ewaybillFeatureOn = Boolean(companyAccess.ewaybill)
  const [gstVerificationOn, setGstVerificationOn] = useState(
    Boolean(companyAccess.gst_verification)
  )
  const [senderGstStatus, setSenderGstStatus] = useState(null)
  const [receiverGstStatus, setReceiverGstStatus] = useState(null)
  const senderLocationLocked = Boolean(user?.location) && !isPickupMan(user)
  const [loading, setLoading] = useState(false)
  const [paymentIntent, setPaymentIntent] = useState(null)
  const [showPaymentQr, setShowPaymentQr] = useState(false)
  const [showCustomArticleType, setShowCustomArticleType] = useState(false)
  const [customArticleType, setCustomArticleType] = useState("")
  const [consignerLocations, setConsignerLocations] = useState([])
  const [consigneeLocations, setConsigneeLocations] = useState([])
  const [senderLocationSearch, setSenderLocationSearch] = useState("")
  const [receiverLocationSearch, setReceiverLocationSearch] = useState("")
  const [showSenderDropdown, setShowSenderDropdown] = useState(false)
  const [showReceiverDropdown, setShowReceiverDropdown] = useState(false)
  const [senderHighlightIndex, setSenderHighlightIndex] = useState(-1)
  const [receiverHighlightIndex, setReceiverHighlightIndex] = useState(-1)

  const [useShipmentClient, setUseShipmentClient] = useState(false)
  const [shipmentClients, setShipmentClients] = useState([])
  const [clientSearch, setClientSearch] = useState("")
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [clientHighlightIndex, setClientHighlightIndex] = useState(-1)
  const [senderSuggestionHighlightIndex, setSenderSuggestionHighlightIndex] = useState(-1)
  const [receiverSuggestionHighlightIndex, setReceiverSuggestionHighlightIndex] = useState(-1)

  const [showChargesModal, setShowChargesModal] = useState(false)
  const [chargesExpanded, setChargesExpanded] = useState(false)
  const [showViewEwayBillModal, setShowViewEwayBillModal] = useState(false)
  const [showCreatePreview, setShowCreatePreview] = useState(false)

  // Sender customer suggestions
  const [senderSuggestions, setSenderSuggestions] = useState([])
  const [showSenderSuggestions, setShowSenderSuggestions] = useState(false)
  const [senderSearchQuery, setSenderSearchQuery] = useState("")
  const [senderSuggestionsLoading, setSenderSuggestionsLoading] = useState(false)

  // Receiver customer suggestions
  const [receiverSuggestions, setReceiverSuggestions] = useState([])
  const [showReceiverSuggestions, setShowReceiverSuggestions] = useState(false)
  const [receiverSearchQuery, setReceiverSearchQuery] = useState("")
  const [receiverSuggestionsLoading, setReceiverSuggestionsLoading] = useState(false)

  // Debounce refs for suggestion API calls
  const senderDebounceRef = useRef(null)
  const receiverDebounceRef = useRef(null)
  const skipCreditRematchRef = useRef('')

  const [formData, setFormData] = useState({
    sender_name: "",
    sender_address: "",
    sender_location: "",
    sender_location_hub_id: "",
    sender_contact: "",
    sender_email: "",
    sender_gst: "",
    receiver_name: "",
    receiver_address: "",
    receiver_location: "",
    receiver_location_hub_id: "",
    receiver_contact: "",
    receiver_email: "",
    receiver_gst: "",
    invoice_number: "",
    customer_invoice_number: "",
    booking_date: getLocalDateString(),
    no_of_articles: 1,
    article_type: "PACKAGE",
    article_amount: "",
    weight: "",
    shipment_value: "",
    freight_charges: "",
    handling_charges: "",
    delivery_charges: "",
    door_pickup_charges: "",
    docket_fee: "0",
    other_charges: "",
    insurance_charges: "",
    discount: "",
    rate_per_kg: "",
    loading_charges: "",
    unloading_charges: "",
    toll_charges: "",
    fuel_surcharge: "",
    packing_charges: "",
    delivery_type: "hub_delivery",
    mode_of_payment: "to_pay",
    payment_mode: "",
    pickup_type: "partial_pickup",
    parcel_detail: "",
    parcel_image_1: null,
    parcel_image_2: null,
    paid_amount: "",
    sgst_percentage: "",
    cgst_percentage: "",
    igst_percentage: "",
  })

  const { errors, validate, validateField } = useValidation()

  // RCM (Reverse Charge Mechanism) company setting
  const [companyRcm, setCompanyRcm] = useState({ enabled: false, igst: 0, cgst: 0, sgst: 0 })
  const [companyGstInfo, setCompanyGstInfo] = useState({ gst_number: "", state: "" })
  const [isCargoMode, setIsCargoMode] = useState(() => !!user?.is_cargo_shipment)
  const [packageLayoutReady, setPackageLayoutReady] = useState(
    () => typeof user?.is_cargo_shipment === 'boolean',
  )
  const cargoModeFromSettingsRef = useRef(false)
  const [busFreightByWeight, setBusFreightByWeight] = useState(false)
  const [showAutoMlSection, setShowAutoMlSection] = useState(false)
  const [chargeDefaults, setChargeDefaults] = useState(resolveCargoDefaults())
  const [parcels, setParcels] = useState(() => defaultParcelsForMode(!!user?.is_cargo_shipment))

  const validationRules = {
    sender_name:      (v) => validators.required(v, 'Sender name'),
    sender_contact:   (v) => validators.required(v, 'Sender contact') || validators.contact(v),
    sender_location:  (v) => validators.required(v, 'Sender location'),
    sender_email:     (v) => v ? validators.email(v) : '',
    sender_gst:       (v) => v ? validators.gst(v) : '',
    receiver_name:    (v) => validators.required(v, 'Receiver name'),
    receiver_contact: (v) => validators.required(v, 'Receiver contact') || validators.contact(v),
    receiver_location:(v) => validators.required(v, 'Receiver location'),
    receiver_email:   (v) => v ? validators.email(v) : '',
    receiver_gst:     (v) => v ? validators.gst(v) : '',
    // Bus multi-article rows validated on submit; cargo via package dimensions
    article_type:     () => '',
    no_of_articles:   () => '',
    article_amount:   () => '',
  }

  const handleBusArticlesChange = (nextArticles) => {
    setParcels(nextArticles)
    setFormData((prev) => ({ ...prev, ...syncFormFromBusArticles(nextArticles) }))
  }

  const handleConfirmModalQtyChange = (index, qty) => {
    const nextQty = Math.max(1, Math.min(9999, Math.floor(Number(qty) || 1)))
    const nextParcels = (parcels || []).map((row, i) => {
      if (i !== index) return row
      const patched = { ...row, qty: nextQty, chargeable_manual: false }
      return isCargoMode ? recomputeParcel(patched) : patched
    })
    if (isCargoMode) {
      setParcels(nextParcels)
      setFormData((prev) => ({ ...prev, no_of_articles: sumParcelQty(nextParcels) }))
      return
    }
    handleBusArticlesChange(nextParcels)
  }

  const [imagePreview, setImagePreview] = useState({ img1: null, img2: null })
  const [shipmentMediaFiles, setShipmentMediaFiles] = useState([])

  useEffect(() => {
    const onMediaError = (e) => {
      if (e?.detail) Alert.error(String(e.detail))
    }
    window.addEventListener('shipment-media-error', onMediaError)
    return () => window.removeEventListener('shipment-media-error', onMediaError)
  }, [])
  const [locationCode, setLocationCode] = useState(null)

  const [autoMlEnabled, setAutoMlEnabled] = useState(false)
  const [autoMlMode, setAutoMlMode] = useState("new")
  const [autoMlVehicleId, setAutoMlVehicleId] = useState("")
  const [autoMlAlreadyLoadedKg, setAutoMlAlreadyLoadedKg] = useState(0)
  const [autoMlAgentUserId, setAutoMlAgentUserId] = useState("")
  const [autoMlCrossingHubIds, setAutoMlCrossingHubIds] = useState([])
  const [autoMlCrossingDestIds, setAutoMlCrossingDestIds] = useState([])
  const [autoMlCrossingSearch, setAutoMlCrossingSearch] = useState("")
  const [showAutoMlCrossing, setShowAutoMlCrossing] = useState(false)
  const [showAutoMlCrossingDest, setShowAutoMlCrossingDest] = useState(false)
  const [selectedExistingMlId, setSelectedExistingMlId] = useState("")
  const [mlSearchQuery, setMlSearchQuery] = useState("")
  const [mlSheetSearch, setMlSheetSearch] = useState("")
  const [showMlSheetDropdown, setShowMlSheetDropdown] = useState(false)
  const [showAgentDropdown, setShowAgentDropdown] = useState(false)
  const [agentSearch, setAgentSearch] = useState("")
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false)
  const [vehicleSearch, setVehicleSearch] = useState("")
  const [autoMlOptions, setAutoMlOptions] = useState({
    vehicles: [],
    agents: [],
    today_ml_sheets: [],
    locked_ml_sheets: [],
    auto_ml_hint: null,
    agent_required: !isExternalNetworkCompany(user),
  })
  const [mlCreationBlock, setMlCreationBlock] = useState({ blocked: false })
  const [autoMlLoading, setAutoMlLoading] = useState(false)
  const isExternalCompany = isExternalNetworkCompany(user)

  const [mobileStep, setMobileStep] = useState(0)
  const mobileStepRef = useRef(0)
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches
  )

  useEffect(() => {
    mobileStepRef.current = mobileStep
  }, [mobileStep])

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const mobileStepPanelClass = (step) => {
    if (!isMobileLayout) return ""
    return mobileStep === step
      ? "flex flex-col flex-1 min-h-0 overflow-y-auto"
      : "hidden"
  }

  const mobileStepPanelClassMulti = (...steps) => {
    if (!isMobileLayout) return ""
    if (!steps.includes(mobileStep)) return "hidden"
    if (mobileStep === 4) {
      return "block w-full min-w-0 overflow-visible pb-28"
    }
    return "flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
  }

  const mobilePaymentPanelVisibility = () => {
    if (!isMobileLayout) return ""
    return mobileStep === 4 ? "block w-full min-w-0 shrink-0" : "hidden"
  }

  const validateMobileStep = (step) => {
    if (step === 0) {
      if (!formData.sender_location?.trim()) {
        Alert.error("Please select a sender location")
        return false
      }
      if (!formData.receiver_location?.trim()) {
        Alert.error("Please select a receiver location")
        return false
      }
      return true
    }
    if (step === 1) {
      return validate(formData, {
        sender_name: validationRules.sender_name,
        sender_contact: validationRules.sender_contact,
      })
    }
    if (step === 2) {
      return validate(formData, {
        receiver_name: validationRules.receiver_name,
        receiver_contact: validationRules.receiver_contact,
      })
    }
    if (step === 3) {
      return validate(formData, {
        article_type: validationRules.article_type,
        no_of_articles: validationRules.no_of_articles,
        article_amount: validationRules.article_amount,
      })
    }
    return true
  }

  const goToNextMobileStep = () => {
    const step = mobileStepRef.current
    if (!validateMobileStep(step)) return
    const nextStep = Math.min(step + 1, MOBILE_STEPS.length - 1)
    mobileStepRef.current = nextStep
    setMobileStep(nextStep)
  }

  const handleFormSubmit = (e) => {
    e?.preventDefault?.()
    if (isMobileLayout) {
      const step = mobileStepRef.current
      if (step < MOBILE_STEPS.length - 1) {
        if (!validateMobileStep(step)) return
        const nextStep = step + 1
        mobileStepRef.current = nextStep
        setMobileStep(nextStep)
        return
      }
    }
    openCreatePreview()
  }

  const handleMobilePrimaryAction = () => {
    if (mobileStepRef.current < MOBILE_STEPS.length - 1) {
      goToNextMobileStep()
      return
    }
    openCreatePreview()
  }

  const handleMobileStepTabClick = (idx) => {
    const current = mobileStepRef.current
    if (idx === current) return
    if (idx < current) {
      mobileStepRef.current = idx
      setMobileStep(idx)
      return
    }
    for (let step = current; step < idx; step += 1) {
      if (!validateMobileStep(step)) return
    }
    mobileStepRef.current = idx
    setMobileStep(idx)
  }

  const goToPrevMobileStep = () => {
    setMobileStep((s) => Math.max(s - 1, 0))
  }

  useEffect(() => {
    fetchLocationHubs()
    fetchShipmentClients()
    fetchCompanySettings()
    
    if (user?.city || user?.location_hub_id) {
      setFormData((prev) => ({
        ...prev,
        sender_location: user.city || prev.sender_location,
        sender_location_hub_id: user.location_hub_id || prev.sender_location_hub_id,
      }))
    }
  }, [user?.city, user?.location_hub_id])

  useEffect(() => {
    if (cargoModeFromSettingsRef.current) return
    if (typeof user?.is_cargo_shipment !== 'boolean') return
    const cargoMode = !!user.is_cargo_shipment
    setIsCargoMode(cargoMode)
    setPackageLayoutReady(true)
    setParcels((prev) => parcelsMatchingMode(prev, cargoMode))
  }, [user?.is_cargo_shipment])

  const fetchCompanySettings = async () => {
    try {
      const res = await companySettingsService.getSettings()
      if (res?.data?.rcm) {
        setCompanyRcm({ enabled: false, igst: 0, cgst: 0, sgst: 0, ...res.data.rcm })
      }
      setCompanyGstInfo({
        gst_number: res?.data?.company_gst_number || "",
        state: res?.data?.company_state || "",
      })
      if (typeof res?.data?.gst_verification === 'boolean') {
        setGstVerificationOn(res.data.gst_verification)
      }
      const insurance = res?.data?.shipment_charge_defaults?.insurance
        || res?.data?.shipment_insurance_charge
        || {}
      const insuranceEnabled = !!insurance.enabled
      const insuranceAmount = Number.parseFloat(insurance.amount) || 0

      const cargoMode = !!res?.data?.is_cargo_shipment
      cargoModeFromSettingsRef.current = true
      setIsCargoMode(cargoMode)
      setPackageLayoutReady(true)
      setBusFreightByWeight(cargoMode ? false : !!res?.data?.bus_freight_by_weight)
      const autoMlAllowed =
        !isExternalNetworkCompany(user) && res?.data?.is_auto_ml_enabled !== false
      setShowAutoMlSection(autoMlAllowed)
      if (!autoMlAllowed) {
        setAutoMlEnabled(false)
      }
      setParcels((prev) => parcelsMatchingMode(prev, cargoMode))
      const defaults = resolveCargoDefaults(
        res?.data?.shipment_charge_defaults || res?.data?.cargo_charge_defaults,
      )
      // Ensure insurance from unified or legacy is present
      defaults.insurance = {
        enabled: insuranceEnabled,
        amount: insuranceAmount,
        mode: insurance.mode === 'percent' ? 'percent' : 'fixed',
      }
      setChargeDefaults(defaults)

      setFormData((prev) => ({
        ...prev,
        insurance_charges: insuranceEnabled
          ? (defaults.insurance.mode === 'percent'
              ? "0"
              : String(insuranceAmount))
          : "0",
        docket_fee: defaults.docket?.enabled ? String(defaults.docket.amount || 0) : "0",
        handling_charges: defaults.handling?.enabled ? String(defaults.handling.amount || 0) : "",
        door_pickup_charges: defaults.door_pickup?.enabled ? String(defaults.door_pickup.amount || 0) : "",
        delivery_charges: defaults.door_delivery?.enabled ? String(defaults.door_delivery.amount || 0) : "",
        loading_charges: defaults.loading.enabled ? String(defaults.loading.amount || 0) : prev.loading_charges,
        unloading_charges: defaults.unloading.enabled ? String(defaults.unloading.amount || 0) : prev.unloading_charges,
        toll_charges: defaults.toll.enabled ? String(defaults.toll.amount || 0) : prev.toll_charges,
        fuel_surcharge: defaults.fuel_surcharge.enabled ? String(defaults.fuel_surcharge.amount || 0) : prev.fuel_surcharge,
        packing_charges: defaults.packing.enabled ? String(defaults.packing.amount || 0) : prev.packing_charges,
        article_type: cargoMode ? (prev.article_type || "PALLET") : prev.article_type,
        article_amount: cargoMode ? (prev.article_amount || "0") : prev.article_amount,
      }))
    } catch (error) {
      // Settings unavailable -> keep session cargo flag / manual GST
      setPackageLayoutReady(typeof user?.is_cargo_shipment === 'boolean')
    }
  }

  // Look up a hub's state by id, falling back to location name match
  const getHubState = (hubId, locationName = null) => {
    const list = [...consignerLocations, ...consigneeLocations]
    if (hubId) {
      const byId = list.find((h) => String(h.id) === String(hubId))
      if (byId?.state) return byId.state
    }
    if (locationName) {
      const name = locationName.trim().toLowerCase()
      const byName = list.find((h) => (h.location || "").trim().toLowerCase() === name)
      if (byName?.state) return byName.state
    }
    return null
  }

  const gstStateCode = (gst) => {
    const v = (gst || "").trim()
    return v.length >= 2 ? v.slice(0, 2).toUpperCase() : null
  }

  const applySameOrDifferentRates = (sameState, fallbackFivePercent = false) =>
    gtaGstRates(sameState, companyRcm, { fallbackFivePercent })

  const billingGstIsInvalid = () => {
    const senderPays = senderPaysMode(formData.mode_of_payment)
    const gst = billingPartyGst(formData.mode_of_payment, formData.sender_gst, formData.receiver_gst)
    const status = senderPays ? senderGstStatus : receiverGstStatus
    return gstinIsInvalid(gst, gstVerificationOn, status)
  }

  const companyLiableHubRates = () => {
    const senderState = (
      getHubState(formData.sender_location_hub_id, formData.sender_location) || ""
    ).trim().toLowerCase()
    const receiverState = (
      getHubState(formData.receiver_location_hub_id, formData.receiver_location) || ""
    ).trim().toLowerCase()
    const sameState = !!(senderState && receiverState && senderState === receiverState)
    return applySameOrDifferentRates(sameState, true)
  }

  // Mirror of backend RCM logic — live preview on the form
  const deriveRcmGst = () => {
    const senderGst = (formData.sender_gst || "").trim()
    const receiverGst = (formData.receiver_gst || "").trim()
    const hasSender = gstinIsRegistered(senderGst, gstVerificationOn, senderGstStatus)
    const hasReceiver = gstinIsRegistered(receiverGst, gstVerificationOn, receiverGstStatus)

    if (billingGstIsInvalid()) {
      return {
        gstPayer: (companyGstInfo.gst_number || "").trim() || null,
        payerLabel: "Company",
        companyLiable: true,
        ...companyLiableHubRates(),
      }
    }

    // Scenario 1 — both have registered GST
    if (hasSender && hasReceiver) {
      const senderPays = senderPaysMode(formData.mode_of_payment)
      const senderCode = gstStateCode(senderGst)
      const receiverCode = gstStateCode(receiverGst)
      const sameState = !!(senderCode && receiverCode && senderCode === receiverCode)
      const rates = applySameOrDifferentRates(sameState)
      return {
        gstPayer: senderPays ? senderGst : receiverGst,
        payerLabel: senderPays ? "Sender" : "Receiver",
        companyLiable: false,
        ...rates,
      }
    }

    // Scenario 2 — exactly one has registered GST
    if (hasSender || hasReceiver) {
      const partyGst = hasSender ? senderGst : receiverGst
      const partyCode = gstStateCode(partyGst)
      const companyCode = gstStateCode(companyGstInfo.gst_number)
      const sameState = !!(partyCode && companyCode && partyCode === companyCode)
      const rates = applySameOrDifferentRates(sameState)
      return {
        gstPayer: partyGst,
        payerLabel: hasSender ? "Sender" : "Receiver",
        companyLiable: false,
        ...rates,
      }
    }

    // Scenario 3 — nobody has registered GST → company GST payer
    return {
      gstPayer: (companyGstInfo.gst_number || "").trim() || null,
      payerLabel: "Company",
      companyLiable: true,
      ...companyLiableHubRates(),
    }
  }

  const rcmActive = !!companyRcm.enabled

  // Auto-apply GST % when RCM is on, or when the billing customer's GST is invalid
  useEffect(() => {
    const invalidCustomer = billingGstIsInvalid()
    if (!rcmActive && !invalidCustomer) return
    const d = rcmActive ? deriveRcmGst() : { ...companyLiableHubRates() }
    const s = String(d.sgst)
    const c = String(d.cgst)
    const i = String(d.igst)
    setFormData((prev) => {
      if (
        prev.sgst_percentage === s &&
        prev.cgst_percentage === c &&
        prev.igst_percentage === i
      ) {
        return prev
      }
      return { ...prev, sgst_percentage: s, cgst_percentage: c, igst_percentage: i }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rcmActive,
    companyRcm,
    companyGstInfo,
    gstVerificationOn,
    senderGstStatus,
    receiverGstStatus,
    formData.sender_gst,
    formData.receiver_gst,
    formData.mode_of_payment,
    formData.sender_location_hub_id,
    formData.receiver_location_hub_id,
    formData.sender_location,
    formData.receiver_location,
    consignerLocations,
    consigneeLocations,
  ])

  const fetchAutoMlOptions = async (senderHubId, receiverHubId) => {
    if (!senderHubId || !receiverHubId) {
      setAutoMlOptions({ vehicles: [], agents: [], today_ml_sheets: [], locked_ml_sheets: [], auto_ml_hint: null, agent_required: !isExternalCompany })
      return
    }
    try {
      setAutoMlLoading(true)
      const response = await shipmentService.getAutoMlOptions(senderHubId, receiverHubId)
      if (response.data?.success === false) {
        throw new Error(response.data?.error || "Failed to load auto ML options")
      }
      const payload = response.data?.data || response.data || {}
      const options = {
        vehicles: payload.vehicles || [],
        agents: payload.agents || [],
        today_ml_sheets: payload.today_ml_sheets || [],
        locked_ml_sheets: payload.locked_ml_sheets || [],
        auto_ml_hint: payload.auto_ml_hint || null,
        agent_required: payload.agent_required !== false && !isExternalCompany,
      }
      const block = payload.ml_creation_block || { blocked: false }
      setMlCreationBlock(block)
      if (block.blocked) {
        setAutoMlEnabled(false)
      }
      setAutoMlOptions(options)
      setAutoMlVehicleId((prev) =>
        prev && options.vehicles.some((v) => String(v.id) === String(prev)) ? prev : ""
      )
      setAutoMlAgentUserId((prev) =>
        prev && options.agents.some((a) => a.user_id === prev)
          ? prev
          : options.agents.length === 1
          ? options.agents[0].user_id
          : ""
      )
      if (options.today_ml_sheets.length > 0) {
        setAutoMlMode("existing")
        setSelectedExistingMlId(options.today_ml_sheets[0].ml_sheet_id)
      } else {
        setAutoMlMode("new")
        setSelectedExistingMlId("")
      }
    } catch (error) {
      setAutoMlOptions({ vehicles: [], agents: [], today_ml_sheets: [], locked_ml_sheets: [], auto_ml_hint: null, agent_required: !isExternalCompany })
      console.error("Failed to load auto ML options:", error)
    } finally {
      setAutoMlLoading(false)
    }
  }

  // keep paid_amount in sync when status is 'paid' and total changes
  useEffect(() => {
    if (formData.mode_of_payment === "paid") {
      const total = calculateTotalAmount()
      if (formData.paid_amount !== total) {
        setFormData(prev => ({ ...prev, paid_amount: total }))
      }
    }
  }, [
    formData.mode_of_payment,
    formData.freight_charges,
    formData.handling_charges,
    formData.delivery_charges,
    formData.door_pickup_charges,
    formData.other_charges,
    formData.docket_fee,
    formData.discount,
    formData.sgst_percentage,
    formData.cgst_percentage,
    formData.igst_percentage,
    formData.no_of_articles,
    formData.article_amount,
    formData.rate_per_kg,
    formData.loading_charges,
    formData.unloading_charges,
    formData.toll_charges,
    formData.fuel_surcharge,
    formData.packing_charges,
    formData.insurance_charges,
    parcels,
    isCargoMode,
  ])

  useEffect(() => {
    const prev = prevModeOfPaymentRef.current
    prevModeOfPaymentRef.current = formData.mode_of_payment

    const isPaid =
      formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance"
    const wasPaid = prev === "paid" || prev === "paid_balance"

    if (isPaid && !wasPaid) {
      if (!isMobileLayout) {
        setShowParcelImages(true)
      }

      if (!isMobileLayout) {
        const timer = setTimeout(() => {
          paidPaymentFieldsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          })
          setTimeout(() => {
            parcelImagesRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          }, 350)
        }, 80)
        return () => clearTimeout(timer)
      }
    }
  }, [formData.mode_of_payment, isMobileLayout])

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (senderDebounceRef.current) {
        clearTimeout(senderDebounceRef.current)
      }
      if (receiverDebounceRef.current) {
        clearTimeout(receiverDebounceRef.current)
      }
    }
  }, [])

  const senderHubId = resolveHubIdFromLocation(
    formData.sender_location,
    formData.sender_location_hub_id,
    consignerLocations,
  )
  const canSelectCrossingHubs = hasShipmentCrossingAccess(user)
  const receiverHubId = resolveHubIdFromLocation(
    formData.receiver_location,
    formData.receiver_location_hub_id,
    consigneeLocations,
  )
  const autoMlHubChoices = useMemo(() => {
    const seen = new Set()
    const rows = []
    for (const hub of [...consignerLocations, ...consigneeLocations]) {
      const id = hub?.id
      if (id == null || seen.has(String(id))) continue
      seen.add(String(id))
      rows.push(hub)
    }
    return rows
  }, [consignerLocations, consigneeLocations])
  useEffect(() => {
    const receiverId = receiverHubId || formData.receiver_location_hub_id
    setAutoMlCrossingDestIds((prev) =>
      prev.filter((id) => String(id) === String(receiverId)),
    )
  }, [receiverHubId, formData.receiver_location_hub_id])
  const senderHub = consignerLocations.find(
    (h) => String(h.id) === String(senderHubId || formData.sender_location_hub_id),
  )

  const filteredSenderLocations = consignerLocations.filter((loc) => {
    const label = formatHubBranchLabel(loc).toLowerCase()
    const code = String(loc.location_code || loc.hub_code || "").toLowerCase()
    const q = senderLocationSearch.toLowerCase()
    return label.includes(q) || code.includes(q) || (loc.location || "").toLowerCase().includes(q)
  })

  const filteredReceiverLocations = consigneeLocations.filter((loc) => {
    if (!isAllowedBookingDestination(senderHub, loc)) return false
    const label = formatHubBranchLabel(loc).toLowerCase()
    const code = String(loc.location_code || loc.hub_code || "").toLowerCase()
    const q = receiverLocationSearch.toLowerCase()
    return label.includes(q) || code.includes(q) || (loc.location || "").toLowerCase().includes(q)
  })
  const senderLocationDisplay = getSelectedLocationDisplay(
    senderHubId || formData.sender_location_hub_id,
    consignerLocations,
    formData.sender_location,
  )
  const receiverLocationDisplay = getSelectedLocationDisplay(
    receiverHubId || formData.receiver_location_hub_id,
    consigneeLocations,
    formData.receiver_location,
  )
  const internalHubDispatchControls = hasInternalHubDispatchControls(user)
  const bookingHub = consignerLocations.find((hub) =>
    String(hub.id) === String(senderHubId || user?.location_hub_id),
  )
  const senderContactClient = useMemo(() => {
    const digits = String(formData.sender_contact || "").replace(/\D/g, "")
    if (digits.length !== 10) return null
    return shipmentClients.find(
      (client) => String(client.contact || "").replace(/\D/g, "") === digits,
    ) || null
  }, [formData.sender_contact, shipmentClients])

  const creditContactAllowed = useShipmentClient || !!senderContactClient

  const paymentStatusOptions = useMemo(() => {
    const options = internalHubDispatchControls
      ? paymentOptionsForHub(bookingHub)
      : [
        { value: 'to_pay', label: 'To Pay' },
        { value: 'paid', label: 'Paid' },
        { value: 'credit', label: 'Credit' },
      ]
    const withCredit = useShipmentClient && !options.some((option) => option.value === 'credit')
      ? [...options, { value: 'credit', label: 'Credit' }]
      : options
    if (creditContactAllowed) return withCredit
    return withCredit.filter((option) => option.value !== 'credit')
  }, [internalHubDispatchControls, bookingHub, useShipmentClient, creditContactAllowed])

  const hubAllowsCredit =
    !internalHubDispatchControls ||
    paymentOptionsForHub(bookingHub).some((option) => option.value === "credit")

  useEffect(() => {
    if (hubAllowsCredit) return
    if (!useShipmentClient) return
    handleUseClientChange(false)
  }, [hubAllowsCredit])

  useEffect(() => {
    if (useShipmentClient) return
    if (paymentStatusOptions.length === 0) return
    if (!paymentStatusOptions.some((option) => option.value === formData.mode_of_payment)) {
      setFormData((prev) => ({ ...prev, mode_of_payment: paymentStatusOptions[0].value }))
    }
  }, [formData.mode_of_payment, paymentStatusOptions, useShipmentClient])

  useEffect(() => {
    if (!useShipmentClient) return
    if (formData.mode_of_payment === "credit") return
    setFormData((prev) => ({
      ...prev,
      mode_of_payment: "credit",
      paid_amount: "",
      payment_mode: "",
    }))
  }, [useShipmentClient, formData.mode_of_payment])

  useEffect(() => {
    if (!formData.receiver_location_hub_id || !senderHub) return
    const receiver = consigneeLocations.find(
      (h) => String(h.id) === String(formData.receiver_location_hub_id),
    )
    if (isAllowedBookingDestination(senderHub, receiver)) return
    setFormData((prev) => ({
      ...prev,
      receiver_location: '',
      receiver_location_hub_id: '',
    }))
  }, [senderHub, formData.receiver_location_hub_id, consigneeLocations])

  useEffect(() => {
    setFormData((prev) => {
      const updates = {}
      const resolvedSender = resolveHubIdFromLocation(
        prev.sender_location,
        prev.sender_location_hub_id,
        consignerLocations,
      )
      const resolvedReceiver = resolveHubIdFromLocation(
        prev.receiver_location,
        prev.receiver_location_hub_id,
        consigneeLocations,
      )
      if (resolvedSender && String(resolvedSender) !== String(prev.sender_location_hub_id)) {
        updates.sender_location_hub_id = resolvedSender
      }
      if (resolvedReceiver && String(resolvedReceiver) !== String(prev.receiver_location_hub_id)) {
        updates.receiver_location_hub_id = resolvedReceiver
      }
      if (Object.keys(updates).length === 0) return prev
      return { ...prev, ...updates }
    })
  }, [
    formData.sender_location,
    formData.receiver_location,
    consignerLocations,
    consigneeLocations,
  ])

  useEffect(() => {
    if (!autoMlEnabled) {
      setAutoMlVehicleId("")
      setAutoMlAgentUserId("")
      setSelectedExistingMlId("")
      setAutoMlMode("new")
      setAutoMlOptions({ vehicles: [], agents: [], today_ml_sheets: [], locked_ml_sheets: [], auto_ml_hint: null })
      return
    }
    fetchAutoMlOptions(senderHubId, receiverHubId)
  }, [
    autoMlEnabled,
    senderHubId,
    receiverHubId,
  ])

  const fetchLocationHubs = async () => {
    try {
      const response = await shipmentService.getAllLocationHubs()
      const allLocations = filterShipmentLocations(response.data.hubs || [])
      setConsignerLocations(allLocations)
      setConsigneeLocations(allLocations)
      setFormData((prev) => {
        const next = { ...prev }
        // Prefer user's assigned hub/branch when From is locked to their location
        if (!next.sender_location_hub_id && user?.location_hub_id) {
          next.sender_location_hub_id = user.location_hub_id
        }
        if (next.sender_location_hub_id) {
          const senderById = allLocations.find(
            (h) => String(h.id) === String(next.sender_location_hub_id),
          )
          if (senderById) {
            next.sender_location = formatHubBranchLabel(senderById)
            next.sender_location_hub_id = senderById.id
          }
        } else if (next.sender_location) {
          const senderMatch = allLocations.find((h) => {
            const label = formatHubBranchLabel(h).toLowerCase()
            const loc = (h.location || "").trim().toLowerCase()
            const target = next.sender_location.trim().toLowerCase()
            return label === target || loc === target
          })
          if (senderMatch?.id) {
            next.sender_location_hub_id = senderMatch.id
            next.sender_location = formatHubBranchLabel(senderMatch)
          }
        }
        if (next.receiver_location_hub_id) {
          const receiverById = allLocations.find(
            (h) => String(h.id) === String(next.receiver_location_hub_id),
          )
          if (receiverById) {
            const senderForCheck = allLocations.find(
              (h) => String(h.id) === String(next.sender_location_hub_id),
            )
            if (!isAllowedBookingDestination(senderForCheck, receiverById)) {
              next.receiver_location = ""
              next.receiver_location_hub_id = ""
            } else {
              next.receiver_location = formatHubBranchLabel(receiverById)
              next.receiver_location_hub_id = receiverById.id
              const hubRate = rateFromHub(receiverById)
              if (hubRate !== null && !String(next.rate_per_kg || '').trim()) {
                next.rate_per_kg = hubRate
              }
            }
          } else {
            next.receiver_location = ""
            next.receiver_location_hub_id = ""
          }
        } else if (next.receiver_location) {
          const senderForCheck = allLocations.find(
            (h) => String(h.id) === String(next.sender_location_hub_id),
          )
          const receiverMatch = allLocations.find((h) => {
            const label = formatHubBranchLabel(h).toLowerCase()
            const loc = (h.location || "").trim().toLowerCase()
            const target = next.receiver_location.trim().toLowerCase()
            return label === target || loc === target
          })
          if (receiverMatch?.id && isAllowedBookingDestination(senderForCheck, receiverMatch)) {
            next.receiver_location_hub_id = receiverMatch.id
            next.receiver_location = formatHubBranchLabel(receiverMatch)
            const hubRate = rateFromHub(receiverMatch)
            if (hubRate !== null && !String(next.rate_per_kg || '').trim()) {
              next.rate_per_kg = hubRate
            }
          } else {
            next.receiver_location = ""
            next.receiver_location_hub_id = ""
          }
        }
        return next
      })
      updateGcPrefix(
        allLocations,
        formData.sender_location_hub_id || user?.location_hub_id,
        formData.receiver_location_hub_id,
        formData.sender_location || user?.city,
        formData.receiver_location,
      )
    } catch (error) {
      console.error("Error fetching location hubs:", error)
      Alert.error("Failed to load location hubs")
    }
  }

  const findLocationHub = (hubs, hubId = null, location = null) => {
    if (!Array.isArray(hubs) || hubs.length === 0) return null
    if (hubId !== null && hubId !== undefined && hubId !== "") {
      const byId = hubs.find((h) => String(h.id) === String(hubId))
      if (byId) return byId
    }
    if (location) {
      return hubs.find(
        (h) =>
          formatHubBranchLabel(h) === location || h.location === location,
      ) || null
    }
    return null
  }

  const updateGcPrefix = (
    hubs = consignerLocations,
    senderHubId = formData.sender_location_hub_id,
    receiverHubId = formData.receiver_location_hub_id,
    senderLocation = formData.sender_location,
    receiverLocation = formData.receiver_location,
  ) => {
    const senderHub = findLocationHub(hubs, senderHubId, senderLocation)
    const receiverHub = findLocationHub(hubs, receiverHubId, receiverLocation)
    const codes = getGcRoutePrefix(senderHub, receiverHub)
    setLocationCode(codes.prefix || null)
  }

  const calculateFreightCharges = () => {
    if (isCargoMode) {
      const totalChargeable = sumChargeableWeight(parcels)
      return calcFreightFromRate(toNum(formData.rate_per_kg), totalChargeable).toFixed(2)
    }
    return calcBusFreight(parcels, formData.rate_per_kg, busFreightByWeight).toFixed(2)
  }

  // Percent insurance: keep Insurance = freight × % (rupee-rounded)
  useEffect(() => {
    if (!chargeDefaults.insurance?.enabled) return
    if (chargeDefaults.insurance?.mode !== 'percent') return
    const freight = Number.parseFloat(calculateFreightCharges()) || 0
    const next = String(calcInsuranceFromSettings(chargeDefaults.insurance, freight))
    setFormData((prev) => (
      prev.insurance_charges === next ? prev : { ...prev, insurance_charges: next }
    ))
  }, [
    chargeDefaults.insurance?.enabled,
    chargeDefaults.insurance?.mode,
    chargeDefaults.insurance?.amount,
    parcels,
    formData.rate_per_kg,
    isCargoMode,
    busFreightByWeight,
  ])

  const parcelCount = sumParcelQty(parcels)

  const scaledCharge = (key, field) =>
    chargeAmountForSubmit(chargeDefaults, key, formData[field], parcelCount)

  const doorServiceCharge = (key, field) => (
    chargeDefaults[key]?.enabled || Number.parseFloat(formData[field] || 0) > 0
      ? scaledCharge(key, field)
      : 0
  )

  const sharedExtraChargesTotal = () => {
    let total = 0
    if (chargeDefaults.loading.enabled) total += scaledCharge('loading', 'loading_charges')
    if (chargeDefaults.toll.enabled) total += toNum(formData.toll_charges)
    if (chargeDefaults.fuel_surcharge.enabled) total += toNum(formData.fuel_surcharge)
    if (chargeDefaults.packing.enabled) total += toNum(formData.packing_charges)
    return total
  }

  const roundRupee = (value) => Math.round(Number(value) || 0)

  const calculateTotalAmount = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = chargeDefaults.handling?.enabled
      ? (Number.parseFloat(formData.handling_charges) || 0)
      : 0
    const deliveryCharges = doorServiceCharge('door_delivery', 'delivery_charges')
    const doorPickupCharges = doorServiceCharge('door_pickup', 'door_pickup_charges')
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const insuranceCharges = chargeDefaults.insurance?.enabled
      ? (Number.parseFloat(formData.insurance_charges) || 0)
      : 0
    const discount = Number.parseFloat(formData.discount) || 0
    const docketFee = chargeDefaults.docket?.enabled
      ? (Number.parseFloat(formData.docket_fee) || 0)
      : 0
    const sharedExtra = sharedExtraChargesTotal()
    
    const subtotalBeforeDiscount = freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + otherCharges + insuranceCharges + sharedExtra
    const subtotal = Math.max(0, subtotalBeforeDiscount - discount)

    let sgstPct = Number.parseFloat(formData.sgst_percentage) || 0
    let cgstPct = Number.parseFloat(formData.cgst_percentage) || 0
    let igstPct = Number.parseFloat(formData.igst_percentage) || 0
    // Manual GST (RCM off): base = discounted subtotal, GST always added to total.
    let gstBase = subtotal
    let addGstToTotal = true
    if (companyRcm.enabled) {
      const d = deriveRcmGst()
      sgstPct = Number(d.sgst) || 0
      cgstPct = Number(d.cgst) || 0
      igstPct = Number(d.igst) || 0
      // Under RCM, GST is calculated on the freight amount only, and only added
      // into the total when the company is liable (Scenario 3 — nobody has GST).
      gstBase = Math.max(0, freightCharges)
      addGstToTotal = !!d.companyLiable
    } else if (billingGstIsInvalid()) {
      const rates = companyLiableHubRates()
      sgstPct = Number(rates.sgst) || 0
      cgstPct = Number(rates.cgst) || 0
      igstPct = Number(rates.igst) || 0
      gstBase = Math.max(0, freightCharges)
      addGstToTotal = true
    }
    if (parcelsAreGstExempt(parcels)) {
      sgstPct = 0
      cgstPct = 0
      igstPct = 0
    }

    // Round each GST line first, then add — matches backend and avoids -1 balance
    const sgst = roundRupee((gstBase * sgstPct) / 100)
    const cgst = roundRupee((gstBase * cgstPct) / 100)
    const igst = roundRupee((gstBase * igstPct) / 100)

    return String(roundRupee(subtotal) + (addGstToTotal ? sgst + cgst + igst : 0))
  }

  const calculateGSTAmounts = () => {
    const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0
    const handlingCharges = chargeDefaults.handling?.enabled
      ? (Number.parseFloat(formData.handling_charges) || 0)
      : 0
    const deliveryCharges = doorServiceCharge('door_delivery', 'delivery_charges')
    const doorPickupCharges = doorServiceCharge('door_pickup', 'door_pickup_charges')
    const otherCharges = Number.parseFloat(formData.other_charges) || 0
    const insuranceCharges = chargeDefaults.insurance?.enabled
      ? (Number.parseFloat(formData.insurance_charges) || 0)
      : 0
    const discount = Number.parseFloat(formData.discount) || 0
    const docketFee = chargeDefaults.docket?.enabled
      ? (Number.parseFloat(formData.docket_fee) || 0)
      : 0
    const sharedExtra = sharedExtraChargesTotal()
    
    const subtotalBeforeDiscount = freightCharges + handlingCharges + deliveryCharges + doorPickupCharges + docketFee + otherCharges + insuranceCharges + sharedExtra
    const subtotal = Math.max(0, subtotalBeforeDiscount - discount)

    let sgstPct = Number.parseFloat(formData.sgst_percentage) || 0
    let cgstPct = Number.parseFloat(formData.cgst_percentage) || 0
    let igstPct = Number.parseFloat(formData.igst_percentage) || 0
    // Under RCM, GST is calculated on the freight amount only.
    let gstBase = subtotal
    if (companyRcm.enabled) {
      const d = deriveRcmGst()
      sgstPct = Number(d.sgst) || 0
      cgstPct = Number(d.cgst) || 0
      igstPct = Number(d.igst) || 0
      gstBase = Math.max(0, freightCharges)
    } else if (billingGstIsInvalid()) {
      const rates = companyLiableHubRates()
      sgstPct = Number(rates.sgst) || 0
      cgstPct = Number(rates.cgst) || 0
      igstPct = Number(rates.igst) || 0
      gstBase = Math.max(0, freightCharges)
    }
    if (parcelsAreGstExempt(parcels)) {
      sgstPct = 0
      cgstPct = 0
      igstPct = 0
    }

    return {
      sgst: String(roundRupee((gstBase * sgstPct) / 100)),
      cgst: String(roundRupee((gstBase * cgstPct) / 100)),
      igst: String(roundRupee((gstBase * igstPct) / 100)),
    }
  }

  const handleChange = (e) => {
    const { name, value, type } = e.target

    const chargeFields = [
      "freight_charges",
      "handling_charges",
      "delivery_charges",
      "door_pickup_charges",
      "other_charges",
      "insurance_charges",
      "docket_fee",
      "discount",
      "paid_amount",
      "article_amount",
      "rate_per_kg",
      "loading_charges",
      "unloading_charges",
      "toll_charges",
      "fuel_surcharge",
      "packing_charges",
      "sgst_percentage",
      "cgst_percentage",
      "igst_percentage",
    ]

    if (name === "article_type") {
      if (value === "OTHER") {
        setShowCustomArticleType(true)
        setFormData((prev) => ({ ...prev, article_type: "" }))
        validateField("article_type", "", validationRules.article_type)
        return
      }
      setShowCustomArticleType(false)
      const upperVal = value.toUpperCase()
      setFormData((prev) => ({ ...prev, article_type: upperVal }))
      validateField("article_type", upperVal, validationRules.article_type)
      return
    }

    if (chargeFields.includes(name)) {
      if (ADDITIONAL_CHARGE_FIELDS.includes(name) && !canEditChargeField(name)) {
        return
      }
      if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
        // Special validation for paid_amount
        if (name === "paid_amount" && value !== "") {
          const paidAmount = parseFloat(value)
          const totalAmount = parseFloat(calculateTotalAmount())
          
          if (paidAmount > totalAmount) {
            Alert.error(`Paid amount (₹${paidAmount.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`)
            return
          }
        }
        setFormData({ ...formData, [name]: value })
        if (validationRules[name]) validateField(name, value, validationRules[name])
      }
    } else {
      let filtered = value
      if (name === 'sender_contact' || name === 'receiver_contact') filtered = inputFilters.digits10(value)
      if (name === 'sender_gst' || name === 'receiver_gst') {
        filtered = inputFilters.uppercase(value).replace(/[^A-Z0-9]/g, '').slice(0, 15)
      }
      
      const updatedFormData = {
        ...formData,
        [name]: type === "number" ? (filtered === "" ? "" : Number.parseFloat(filtered)) : filtered,
      }

      // Handle payment status changes
      if (name === "mode_of_payment") {
        if (value === "to_pay" || value === "credit") {
          updatedFormData.paid_amount = ""
          updatedFormData.payment_mode = ""
        } else if (value === "paid") {
          // when switched to paid, auto-fill paid amount with total and default to cash
          updatedFormData.payment_mode = "cash"
          updatedFormData.paid_amount = calculateTotalAmount()
        } else if (value === "paid_balance") {
          updatedFormData.payment_mode = "cash"
          updatedFormData.paid_amount = ""
        }
      }

      // Handle payment mode selection - auto-fill for "paid" status only
      if (name === "payment_mode") {
        if (!formData.paid_amount) {
          updatedFormData.paid_amount = ""
        }
      }

      setFormData(updatedFormData)
      if (validationRules[name]) validateField(name, updatedFormData[name], validationRules[name])
    }
  }

  const handleSubmitEwayBills = async () => {
    const validBills = ewayBills.filter(isValidEway)
    if (validBills.length === 0) {
      Alert.error("Please enter at least one valid E-way bill")
      return
    }
    
    // Check for duplicates
    const allBills = [...submittedEwayBills, ...validBills]
    const uniqueBills = [...new Set(allBills)]
    
    if (uniqueBills.length !== allBills.length) {
      Alert.error("Duplicate E-way bill numbers found")
      return
    }

    if (!ewaybillFeatureOn) {
      setSubmittedEwayBills(uniqueBills)
      Alert.success(`${validBills.length} E-way bill(s) added successfully`)
      handleCloseEwayBillModal()
      return
    }

    const newBills = validBills.filter((bill) => !submittedEwayBills.includes(bill))
    if (newBills.length === 0) {
      handleCloseEwayBillModal()
      return
    }

    setEwayLookupLoading(true)
    try {
      let firstFields = null
      for (const ewbNo of newBills) {
        const response = await shipmentService.lookupEwayBill(ewbNo)
        if (!firstFields) {
          firstFields = response?.data?.shipment_fields || {}
        }
      }
      if (firstFields) {
        setFormData((prev) => applyEwayFieldsToForm(prev, firstFields))
      }
      setSubmittedEwayBills(uniqueBills)
      Alert.success(`${newBills.length} E-way bill(s) fetched and added successfully`)
      handleCloseEwayBillModal()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to fetch E-way bill details. Check the number and try again.")
    } finally {
      setEwayLookupLoading(false)
    }
  }

  const handleViewEwayBills = () => {
    if (submittedEwayBills.length === 0) {
      Alert.info("No E-way bills added yet")
      return
    }
    setShowViewEwayBillModal(true)
  }

  const handleRemoveEwayBill = (indexToRemove) => {
    const updated = submittedEwayBills.filter((_, index) => index !== indexToRemove)
    setSubmittedEwayBills(updated)
    if (updated.length === 0) {
      setShowViewEwayBillModal(false)
    }
    Alert.success("E-way bill removed successfully")
  }

  const fetchShipmentClients = async () => {
    try {
      const response = await shipmentClientService.getAll(1, 1000, { status: "active" })
      setShipmentClients(response.data || [])
    } catch (error) {
      console.error("Error fetching shipment clients:", error)
      Alert.error("Failed to load shipment clients")
    }
  }

  const filteredClients = shipmentClients.filter(
    (client) =>
      client.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
      client.company_name.toLowerCase().includes(clientSearch.toLowerCase()),
  )

  const isCreditClientContact = (contact) => {
    const digits = String(contact || "").replace(/\D/g, "")
    if (!digits) return false
    return shipmentClients.some(
      (client) => String(client.contact || "").replace(/\D/g, "") === digits,
    )
  }

  const handleClientSelect = (client) => {
    setSelectedClient(client)
    setFormData((prev) => ({
      ...prev,
      sender_name: client.full_name,
      sender_address: client.address,
      sender_contact: client.contact,
      sender_email: client.email,
      sender_gst: client.gst_number || "",
      shipment_client_id: client.id,
      mode_of_payment: "credit",
      paid_amount: "",
      payment_mode: "",
    }))
    setClientSearch("")
    setShowClientDropdown(false)
    setClientHighlightIndex(-1)
  }

  const handleUseClientChange = (checked) => {
    setUseShipmentClient(checked)
    if (checked) {
      skipCreditRematchRef.current = ""
      setFormData((prev) => ({
        ...prev,
        mode_of_payment: "credit",
        paid_amount: "",
        payment_mode: "",
      }))
      return
    }
    skipCreditRematchRef.current = String(formData.sender_contact || "").replace(/\D/g, "")
    setSelectedClient(null)
    setFormData({
      ...formData,
      sender_name: "",
      sender_address: "",
      sender_contact: "",
      sender_email: "",
      sender_gst: "",
      shipment_client_id: null,
      mode_of_payment: formData.mode_of_payment === "credit" ? "to_pay" : formData.mode_of_payment,
    })
  }

  useEffect(() => {
    if (!hubAllowsCredit) return
    if (useShipmentClient || selectedClient) return
    const digits = String(formData.sender_contact || "").replace(/\D/g, "")
    if (digits.length !== 10) return
    if (skipCreditRematchRef.current === digits) return
    const match = shipmentClients.find(
      (client) => String(client.contact || "").replace(/\D/g, "") === digits,
    )
    if (!match) return
    setUseShipmentClient(true)
    handleClientSelect(match)
  }, [formData.sender_contact, shipmentClients, useShipmentClient, selectedClient])

  // Fetch sender customer suggestions by full contact number only (debounced)
  const fetchSenderSuggestions = async (searchQuery) => {
    if (senderDebounceRef.current) {
      clearTimeout(senderDebounceRef.current)
    }

    const digits = String(searchQuery || "").replace(/\D/g, "")
    if (digits.length < 10) {
      setSenderSuggestions([])
      setShowSenderSuggestions(false)
      setSenderSuggestionsLoading(false)
      return
    }

    setShowSenderSuggestions(true)
    setSenderSuggestionsLoading(true)

    // Debounce so paste/rapid edits don't spam the server
    senderDebounceRef.current = setTimeout(async () => {
      try {
        const response = await shipmentService.getCustomerSuggestions(digits)
        setSenderSuggestions(
          shipmentService.mergeCustomerSuggestions(
            response.data?.data?.senders,
          )
        )
      } catch (error) {
        console.error("Error fetching sender suggestions:", error)
        setSenderSuggestions([])
      } finally {
        setSenderSuggestionsLoading(false)
      }
    }, 400)
  }

  // Fetch receiver customer suggestions by full contact number only (debounced).
  // Also includes past senders + current consignors details for same-person bookings.
  const fetchReceiverSuggestions = async (searchQuery) => {
    if (receiverDebounceRef.current) {
      clearTimeout(receiverDebounceRef.current)
    }

    const digits = String(searchQuery || "").replace(/\D/g, "")
    if (digits.length < 10) {
      setReceiverSuggestions([])
      setShowReceiverSuggestions(false)
      setReceiverSuggestionsLoading(false)
      return
    }

    setShowReceiverSuggestions(true)
    setReceiverSuggestionsLoading(true)

    receiverDebounceRef.current = setTimeout(async () => {
      try {
        const response = await shipmentService.getCustomerSuggestions(digits)
        setReceiverSuggestions(
          shipmentService.mergeCustomerSuggestions(
            response.data?.data?.receivers,
          )
        )
      } catch (error) {
        console.error("Error fetching receiver suggestions:", error)
        setReceiverSuggestions([])
      } finally {
        setReceiverSuggestionsLoading(false)
      }
    }, 400)
  }

  // Handle sender suggestion selected — also fills the receiver from that same past booking
  const handleSenderSuggestionSelect = (suggestion) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        sender_name: suggestion.name || "",
        sender_contact: suggestion.contact || "",
        sender_address: suggestion.address || "",
        sender_email: suggestion.email || "",
        sender_gst: suggestion.gst || "",
      }
      const receiver = suggestion.paired_receiver
      if (receiver && !isCreditClientContact(suggestion.contact)) {
        next.receiver_name = receiver.name || ""
        next.receiver_contact = receiver.contact || ""
        next.receiver_address = receiver.address || ""
        next.receiver_email = receiver.email || ""
        next.receiver_gst = receiver.gst || ""
      }
      return next
    })
    setSenderSearchQuery("")
    setShowSenderSuggestions(false)
    setSenderSuggestions([])
    setSenderSuggestionHighlightIndex(-1)
  }

  // Handle receiver suggestion selected — also fills the sender from that same past booking
  const handleReceiverSuggestionSelect = (suggestion) => {
    if (isCreditClientContact(suggestion.contact)) {
      const digits = String(suggestion.contact || "").replace(/\D/g, "")
      const client = shipmentClients.find(
        (row) => String(row.contact || "").replace(/\D/g, "") === digits,
      )
      if (client) handleClientSelect(client)
      setReceiverSearchQuery("")
      setShowReceiverSuggestions(false)
      setReceiverSuggestions([])
      setReceiverSuggestionHighlightIndex(-1)
      return
    }
    setFormData((prev) => {
      const next = {
        ...prev,
        receiver_name: suggestion.name || "",
        receiver_contact: suggestion.contact || "",
        receiver_address: suggestion.address || "",
        receiver_email: suggestion.email || "",
        receiver_gst: suggestion.gst || "",
      }
      const sender = suggestion.paired_sender
      if (sender) {
        next.sender_name = sender.name || ""
        next.sender_contact = sender.contact || ""
        next.sender_address = sender.address || ""
        next.sender_email = sender.email || ""
        next.sender_gst = sender.gst || ""
      }
      return next
    })
    setReceiverSearchQuery("")
    setShowReceiverSuggestions(false)
    setReceiverSuggestions([])
    setReceiverSuggestionHighlightIndex(-1)
  }

  const handleImageChange = (e) => {
    const { name } = e.target
    const file = e.target.files?.[0]
    if (file) {
      setFormData({ ...formData, [name]: file })
      const reader = new FileReader()
      reader.onload = (e) => {
        if (name === "parcel_image_1") {
          setImagePreview({ ...imagePreview, img1: e.target.result })
        } else {
          setImagePreview({ ...imagePreview, img2: e.target.result })
        }
      }
      reader.readAsDataURL(file)
    }
  }

  // Cargo overweight check for auto ML "new" mode: shipment vs remaining
  // same-day capacity on the selected vehicle.
  const autoMlSelectedVehicle = autoMlOptions.vehicles.find(
    (v) => String(v.id) === String(autoMlVehicleId)
  )
  const autoMlShipmentWeight = isCargoMode ? sumChargeableWeight(parcels) : 0
  const autoMlCapacityKg = getVehicleCapacityKg(autoMlSelectedVehicle)
  const autoMlAvailableKg =
    autoMlCapacityKg != null
      ? Math.max(0, Math.round(autoMlCapacityKg - (Number(autoMlAlreadyLoadedKg) || 0)))
      : null
  const autoMlOverweight =
    isCargoMode &&
    showAutoMlSection &&
    autoMlEnabled &&
    autoMlMode === "new" &&
    autoMlAvailableKg != null &&
    autoMlShipmentWeight > autoMlAvailableKg

  useEffect(() => {
    let cancelled = false
    if (!isCargoMode || autoMlMode !== "new" || !autoMlVehicleId) {
      setAutoMlAlreadyLoadedKg(0)
      return undefined
    }
    ;(async () => {
      try {
        const res = await mlSheetService.getVehicleDayCapacity({
          vehicleId: autoMlVehicleId,
        })
        const kg = res?.data?.data?.already_loaded_kg
        if (!cancelled) setAutoMlAlreadyLoadedKg(Number(kg) || 0)
      } catch {
        if (!cancelled) setAutoMlAlreadyLoadedKg(0)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isCargoMode, autoMlMode, autoMlVehicleId])

  const validateShipmentForCreate = () => {
    if (!validate(formData, validationRules)) {
      Alert.error("Please fill all required fields correctly before submitting.")
      return false
    }
    if (!formData.sender_location || formData.sender_location.trim() === "") {
      Alert.error("Please select a sender location")
      return false
    }
    if (!formData.receiver_location || formData.receiver_location.trim() === "") {
      Alert.error("Please select a receiver location")
      return false
    }
    const phoneRegex = /^[0-9]\d{9}$/
    if (!phoneRegex.test(formData.sender_contact)) {
      Alert.error("Sender contact must be a valid 10-digit mobile number")
      return false
    }
    if (!phoneRegex.test(formData.receiver_contact)) {
      Alert.error("Receiver contact must be a valid 10-digit mobile number")
      return false
    }
    const shipmentValue = Number.parseFloat(formData.shipment_value) || 0
    if (ewaybillFeatureOn && shipmentValue > EWAY_BILL_MANDATORY_VALUE && submittedEwayBills.length === 0) {
      Alert.error("E-way bill number is required when shipment value exceeds ₹50,000")
      return false
    }
    if (isCargoMode) {
      const hasValidParcel = parcels.some((p) => toNum(p.actual_weight) > 0 || toNum(p.chargeable_weight) > 0)
      if (!hasValidParcel) {
        Alert.error("Please add at least one package with weight for cargo shipment")
        return false
      }
    } else {
      const hasArticleType = parcels.some((p) => (p.article_type || "").toString().trim())
      if (!hasArticleType) {
        Alert.error("Please add at least one article with a type")
        return false
      }
      if (busFreightByWeight) {
        const missingWeight = firstBusArticleWithoutCharge(parcels, true)
        if (missingWeight) {
          Alert.error(`Enter the weight for article ${missingWeight}`)
          return false
        }
        if (toNum(formData.rate_per_kg) <= 0) {
          Alert.error("Enter the rate per kg. Charges are required to create the shipment")
          return false
        }
      } else {
        const missingCharge = firstBusArticleWithoutCharge(parcels, false)
        if (missingCharge) {
          Alert.error(`Enter the article charges for article ${missingCharge}. Charges are required to create the shipment`)
          return false
        }
      }
    }
    if (formData.mode_of_payment === "credit") {
      const digits = String(formData.sender_contact || "").replace(/\D/g, "")
      const isCreditClient = shipmentClients.some(
        (client) => String(client.contact || "").replace(/\D/g, "") === digits,
      )
      if (!isCreditClient) {
        Alert.error("Credit is only for shipment clients. This contact is not a credit customer.")
        return false
      }
    }
    if (formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") {
      const paidAmount = Number.parseFloat(formData.paid_amount) || 0
      const totalAmount = Number.parseFloat(calculateTotalAmount())
      if (paidAmount > totalAmount) {
        Alert.error(`Paid amount (₹${paidAmount.toFixed(2)}) cannot exceed total amount (₹${totalAmount.toFixed(2)})`)
        return false
      }
      if (!formData.payment_mode) {
        Alert.error("Please select a Payment Mode")
        return false
      }
    }
    if (showAutoMlSection && autoMlEnabled) {
      if (!senderHubId || !receiverHubId) {
        Alert.error("Select sender and receiver locations before enabling auto ML creation")
        return false
      }
      if (autoMlOptions.agent_required && !autoMlAgentUserId) {
        Alert.error("Please select an agent for auto ML creation")
        return false
      }
      if (autoMlMode === "new" && !autoMlVehicleId) {
        Alert.error("Please select a vehicle for auto ML creation")
        return false
      }
      if (autoMlOverweight) {
        Alert.error(
          `This shipment (${formatKg(autoMlShipmentWeight)}) exceeds available capacity today ` +
            `(${formatKg(autoMlAvailableKg)} of ${formatKg(autoMlCapacityKg)}; ` +
            `already loaded ${formatKg(autoMlAlreadyLoadedKg)}). Choose another vehicle.`
        )
        return false
      }
      if (autoMlMode === "existing" && !selectedExistingMlId) {
        Alert.error("Please select an existing ML sheet for today")
        return false
      }
      if (
        autoMlMode === "new" &&
        canSelectCrossingHubs &&
        autoMlCrossingHubIds.length > 0 &&
        autoMlCrossingDestIds.length === 0
      ) {
        Alert.error("Select the locations that need to be crossed")
        return false
      }
    }
    return true
  }

  const openCreatePreview = () => {
    if (!validateShipmentForCreate()) return
    setShowCreatePreview(true)
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    if (!validateShipmentForCreate()) return
    try {
      setLoading(true)

      const freightCharges = Number.parseFloat(calculateFreightCharges()) || 0

      const busSync = isCargoMode ? null : syncFormFromBusArticles(parcels)
      const totalChargeable = isCargoMode
        ? sumChargeableWeight(parcels)
        : (busFreightByWeight
            ? sumBusArticleWeight(parcels)
            : (Number.parseFloat(formData.weight) || null))
      const submitData = {
        ...formData,
        eway_bill_numbers: submittedEwayBills,
        shipment_id_type: "auto",
        shipment_type: isCargoMode ? "cargo" : "bus",
        created_at: formData.booking_date,
        manual_shipment_id: "",
        no_of_articles: isCargoMode ? sumParcelQty(parcels) : busSync.no_of_articles,
        article_amount: isCargoMode ? 0 : (busFreightByWeight ? 0 : busSync.article_amount),
        article_type: isCargoMode ? (formData.article_type || "PALLET") : busSync.article_type,
        weight: totalChargeable,
        freight_charges: freightCharges,
        handling_charges: chargeDefaults.handling?.enabled
          ? (Number.parseFloat(formData.handling_charges) || 0)
          : 0,
        delivery_charges: doorServiceCharge('door_delivery', 'delivery_charges'),
        door_pickup_charges: doorServiceCharge('door_pickup', 'door_pickup_charges'),
        docket_fee: chargeDefaults.docket?.enabled
          ? (Number.parseFloat(formData.docket_fee) || 0)
          : 0,
        other_charges: Number.parseFloat(formData.other_charges) || 0,
        insurance_charges: chargeDefaults.insurance?.enabled
          ? (Number.parseFloat(formData.insurance_charges) || 0)
          : 0,
        discount: Number.parseFloat(formData.discount) || 0,
        rate_per_kg: (isCargoMode || busFreightByWeight) ? toNum(formData.rate_per_kg) : 0,
        loading_charges: chargeDefaults.loading.enabled ? scaledCharge('loading', 'loading_charges') : 0,
        unloading_charges: 0,
        toll_charges: chargeDefaults.toll.enabled ? toNum(formData.toll_charges) : 0,
        fuel_surcharge: chargeDefaults.fuel_surcharge.enabled ? toNum(formData.fuel_surcharge) : 0,
        packing_charges: chargeDefaults.packing.enabled ? toNum(formData.packing_charges) : 0,
        parcels: parcelsForApi(parcels),
        sgst_percentage: parcelsAreGstExempt(parcels)
          ? 0
          : companyRcm.enabled
          ? (Number(deriveRcmGst().sgst) || 0)
          : billingGstIsInvalid()
          ? (Number(companyLiableHubRates().sgst) || 0)
          : (Number.parseFloat(formData.sgst_percentage) || 0),
        cgst_percentage: parcelsAreGstExempt(parcels)
          ? 0
          : companyRcm.enabled
          ? (Number(deriveRcmGst().cgst) || 0)
          : billingGstIsInvalid()
          ? (Number(companyLiableHubRates().cgst) || 0)
          : (Number.parseFloat(formData.cgst_percentage) || 0),
        igst_percentage: parcelsAreGstExempt(parcels)
          ? 0
          : companyRcm.enabled
          ? (Number(deriveRcmGst().igst) || 0)
          : billingGstIsInvalid()
          ? (Number(companyLiableHubRates().igst) || 0)
          : (Number.parseFloat(formData.igst_percentage) || 0),
        paid_amount: Number.parseFloat(formData.paid_amount) || 0,
        payment_mode: formData.payment_mode || null,
        client_id: user?.user_id,
        client_role: user?.role,
        created_by: user?.user_id,
        auto_ml_enabled: showAutoMlSection && autoMlEnabled,
        auto_ml_mode: autoMlMode,
        auto_ml_vehicle_id: autoMlMode === "new" ? autoMlVehicleId : "",
        auto_ml_agent_user_id: autoMlAgentUserId,
        auto_ml_crossing_location_hub_ids:
          showAutoMlSection && autoMlEnabled && autoMlMode === "new" && canSelectCrossingHubs
            ? autoMlCrossingHubIds
            : [],
        auto_ml_crossing_destination_hub_ids:
          showAutoMlSection && autoMlEnabled && autoMlMode === "new" && canSelectCrossingHubs && autoMlCrossingHubIds.length > 0
            ? autoMlCrossingDestIds
            : null,
        existing_ml_sheet_id: autoMlMode === "existing" ? selectedExistingMlId : "",
        sender_location_hub_id: senderHubId || formData.sender_location_hub_id,
        receiver_location_hub_id: receiverHubId || formData.receiver_location_hub_id,
        shipment_media: shipmentMediaFiles,
      }
      const response = await shipmentService.createShipment(submitData)
      if (response.data?.requires_online_payment && response.data?.payment_intent) {
        setShowCreatePreview(false)
        setPaymentIntent(response.data.payment_intent)
        setShowPaymentQr(true)
        setLoading(false)
        return
      }
      const mlSheetId = response.data?.ml_sheet?.ml_sheet_id
      const created = response.data?.shipment || response.data?.data || {}
      const gc = created.shipment_id || ""
      const cn = created.consignment_no || ""
      const baseMsg = gc
        ? `Shipment created — GC: ${gc}${cn ? `, CN: ${cn}` : ""}`
        : "Shipment created successfully"
      Alert.success(
        mlSheetId
          ? `${baseMsg}. Added to ML sheet ${mlSheetId}`
          : baseMsg
      )
      setShowCreatePreview(false)
      navigate("/shipments")
    } catch (error) {
      console.error("Error creating shipment:", error)
      Alert.error(error.response?.data?.error || "Failed to create shipment")
      const errMsg = error.response?.data?.error || ''
      if (/locked|payment|ML sheet/i.test(errMsg)) {
        Alert.info('This ML sheet is locked after payment. Switch to "Create new ML" and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleOnlinePaymentSuccess = (intent) => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    const created = intent?.result?.shipment || {}
    const mlSheetId = intent?.result?.ml_sheet?.ml_sheet_id
    const gc = created.shipment_id || intent?.result?.shipment_id || ""
    const cn = created.consignment_no || intent?.result?.consignment_no || ""
    const baseMsg = gc
      ? `Payment received. Shipment created — GC: ${gc}${cn ? `, CN: ${cn}` : ""}`
      : "Payment received. Shipment created successfully"
    Alert.success(mlSheetId ? `${baseMsg}. Added to ML sheet ${mlSheetId}` : baseMsg)
    navigate("/shipments")
  }

  const handleOnlinePaymentCancel = () => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    Alert.info("Online payment cancelled. Shipment was not created.")
  }

  const calculateBalanceAmount = () => {
    const totalAmount = Number.parseFloat(calculateTotalAmount())
    const paid = Number.parseFloat(formData.paid_amount) || 0
    return String(Math.round(totalAmount - paid))
  }

  const handleEnterAsTab = (e) => {
  if (e.key === "Enter") {
    e.preventDefault();

    const form = e.target.form;
    if (!form) return;

    const elements = Array.from(
      form.querySelectorAll(
        "input:not([type=hidden]):not([disabled]):not([type=checkbox]):not([type=radio]), select:not([disabled]), textarea:not([disabled])"
      )
    ).filter((el) => el.offsetParent !== null);

    const submitBtn =
      !isMobileLayout || mobileStepRef.current >= MOBILE_STEPS.length - 1
        ? document.getElementById("create-shipment-submit") ||
          document.getElementById("create-shipment-submit-mobile")
        : null;
    if (submitBtn) elements.push(submitBtn);

    const index = elements.indexOf(e.target);
    if (index > -1 && index + 1 < elements.length) {
      elements[index + 1].focus();
    }
  }
};

  const handleLocationDropdownKeyDown = (e, config) =>
    handleSuggestionDropdownKeyDown(e, { ...config, onEnterFallback: handleEnterAsTab })

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (senderRef.current && !senderRef.current.contains(event.target)) {
        setShowSenderDropdown(false)
      }

      if (receiverRef.current && !receiverRef.current.contains(event.target)) {
        setShowReceiverDropdown(false)
      }

      if (mlSheetDropdownRef.current && !mlSheetDropdownRef.current.contains(event.target)) {
        setShowMlSheetDropdown(false)
        setMlSheetSearch("")
      }

      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false)
        setAgentSearch("")
      }

      if (vehicleDropdownRef.current && !vehicleDropdownRef.current.contains(event.target)) {
          setShowVehicleDropdown(false)
          setVehicleSearch("")
        }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])


  const isConsignorSuggestionsOpen =
    !isMobileLayout &&
    ((showSenderSuggestions && (senderSuggestions.length > 0 || senderSuggestionsLoading)) ||
      (useShipmentClient && showClientDropdown && filteredClients.length > 0))

  const isConsigneeSuggestionsOpen =
    !isMobileLayout &&
    showReceiverSuggestions &&
    (receiverSuggestions.length > 0 || receiverSuggestionsLoading)

  const gcPayCode = getGcPaymentCode(formData.mode_of_payment)
  const gcYearSuffix = getGcYearSuffix()

  return (
    <DashboardLayout fullWidth fillViewport>
                <form id="add-shipment-form" noValidate={isMobileLayout} onSubmit={handleFormSubmit} className={`min-w-0 flex flex-col flex-1 min-h-0 md:[&_input]:py-1.5 md:[&_select]:py-1.5 md:[&_textarea]:py-1.5 ${isMobileLayout ? (mobileStep === 4 ? "overflow-y-auto pb-20" : "overflow-hidden pb-20") : "md:overflow-hidden"}`}>

                {/* Mobile step wizard — md and up unchanged */}
                {isMobileLayout && (
                  <div className="sticky top-0 z-30 bg-white shrink-0 mb-3 pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-1 mb-2">
                      {MOBILE_STEPS.map((step, idx) => (
                        <button
                          key={step.id}
                          type="button"
                          onClick={() => handleMobileStepTabClick(idx)}
                          className={`flex-1 min-w-0 text-center py-1.5 px-0.5 rounded-lg text-[10px] font-semibold transition truncate ${
                            idx === mobileStep
                              ? "bg-orange-500 text-white shadow-sm"
                              : idx < mobileStep
                                ? "bg-orange-100 text-orange-700"
                                : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {step.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <h1 className="flex items-center gap-1.5 text-sm font-bold text-gray-700 truncate">
                        <MapPin className="w-4 h-4 text-buttonhoverTxt shrink-0" />
                        <span className="truncate">{MOBILE_STEPS[mobileStep].label}</span>
                      </h1>
                      <Button variant="secondary" type="button" size="sm" onClick={() => navigate("/shipments")}>
                        Cancel
                      </Button>
                    </div>
                    <div className="mt-2 bg-white border border-gray-200 rounded-xl px-2 py-1.5 shadow-sm">
                      <PaymentStatusToggle
                        options={paymentStatusOptions}
                        value={formData.mode_of_payment}
                        disabled={useShipmentClient}
                        onChange={(value) => handleChange({ target: { name: "mode_of_payment", value } })}
                      />
                    </div>
                  </div>
                )}

                {/* Desktop / tablet header — pinned above scroll area */}
                <div className="hidden md:block mb-2 space-y-2 shrink-0 z-30 bg-gray-50/95 pb-2 border-b border-gray-200/80">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h1 className="flex items-center gap-2 text-base sm:text-xl font-bold text-gray-700 truncate">
                      <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-buttonhoverTxt shrink-0" />
                      <span className="truncate">Add New Shipment</span>
                    </h1>

                    <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3 shrink-0">
                      <div className="bg-white border border-gray-200 rounded-xl px-2.5 py-1.5 shadow-sm">
                        <PaymentStatusToggle
                          options={paymentStatusOptions}
                          value={formData.mode_of_payment}
                          disabled={useShipmentClient}
                          onChange={(value) => handleChange({ target: { name: "mode_of_payment", value } })}
                        />
                      </div>
                      <Button variant="secondary" type="button" onClick={() => navigate("/shipments")}>
                        Cancel
                      </Button>
                      <Button variant="secondary" icon={ArrowLeft} type="button" onClick={() => navigate("/shipments")} className="hidden sm:inline-flex">
                        Back to Dashboard
                      </Button>
                      <Button
                        id="create-shipment-submit"
                        variant="primary"
                        type="button"
                        onClick={handleFormSubmit}
                        loading={loading}
                        disabled={loading || autoMlOverweight}
                        className="hidden md:inline-flex"
                      >
                        {loading ? "Creating..." : "Create Shipment"}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Desktop/iPad: scrollable form body — header + Create Shipment stay visible above */}
                <div className={isMobileLayout ? "contents" : "md:flex-1 md:min-h-0 md:overflow-y-auto md:overflow-x-hidden md:min-w-0 md:pb-24"}>

                {/* Step 0 (mobile) / top row (desktop): destination first; Auto ML below on mobile via order-2 */}
                <div className={`${mobileStepPanelClass(0)} md:!block md:shrink-0 md:mb-2 md:space-y-2 max-md:gap-3`}>
                  {showAutoMlSection && (
                  <div className="max-md:order-2">
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2">
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${autoMlEnabled ? "bg-emerald-500 animate-pulse" : "bg-gray-400"}`}></div>
                          <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">Auto ML Creation</span>
                          {autoMlEnabled && !mlCreationBlock.blocked && autoMlOptions.today_ml_sheets.length > 0 && (
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                                <input
                                  type="radio"
                                  name="auto_ml_mode"
                                  checked={autoMlMode === "existing"}
                                  onChange={() => setAutoMlMode("existing")}
                                  className="text-emerald-600"
                                />
                                Existing
                              </label>
                              <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                                <input
                                  type="radio"
                                  name="auto_ml_mode"
                                  checked={autoMlMode === "new"}
                                  onChange={() => setAutoMlMode("new")}
                                  className="text-emerald-600"
                                />
                                New
                              </label>
                            </div>
                          )}
                        </div>
                        {mlCreationBlock.blocked ? (
                          <span className="text-[11px] text-red-700 truncate" title={mlCreationBlock.reason}>{mlCreationBlock.reason}</span>
                        ) : (
                          <label className="flex items-center gap-2 select-none cursor-pointer shrink-0">
                            <div
                              onClick={() => {
                                if (!mlCreationBlock.blocked) setAutoMlEnabled((prev) => !prev)
                              }}
                              className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${
                                autoMlEnabled ? "bg-emerald-500" : "bg-gray-300"
                              }`}
                            >
                              <span
                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                  autoMlEnabled ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </div>
                            <span className="text-xs text-gray-600 font-medium whitespace-nowrap">
                              {autoMlEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </label>
                        )}
                      </div>

                          {autoMlEnabled && (
                            <div className="mt-1.5 min-w-0">
                              {!senderHubId || !receiverHubId ? (
                                <span className="text-xs text-amber-700 font-medium">
                                  Select sender and receiver locations below to load ML options
                                </span>
                              ) : autoMlLoading ? (
                                <span className="text-xs text-gray-500 italic">Loading vehicles, agents, and ML sheets...</span>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 min-w-0">
                                  {autoMlMode === "existing" && autoMlOptions.today_ml_sheets.length > 0 && (() => {
                                    const filteredMlSheets = autoMlOptions.today_ml_sheets.filter((sheet) => {
                                      if (!mlSheetSearch.trim()) return true
                                      const fromLoc = sheet.from_locations?.[0]?.location || ""
                                      const toLoc = sheet.to_locations?.[0]?.location || ""
                                      const shipmentCount = (sheet.shipment_ids || []).length
                                      const label = `${sheet.ml_sheet_id} ${fromLoc} ${toLoc} ${sheet.vehicle_number || ""} ${shipmentCount}`.toLowerCase()
                                      const query = mlSheetSearch.toLowerCase()
                                      return query.split(/\s+/).every((word) => label.includes(word))
                                    })
                                    const selectedSheet = autoMlOptions.today_ml_sheets.find((s) => s.ml_sheet_id === selectedExistingMlId)
                                    const getSheetLabel = (sheet) => {
                                      const fromLoc = sheet.from_locations?.[0]?.location || ""
                                      const toLoc = sheet.to_locations?.[0]?.location || ""
                                      const routeLabel = fromLoc && toLoc ? ` - ${fromLoc} to ${toLoc}` : ""
                                      const vehicleLabel = sheet.vehicle_number ? ` | ${sheet.vehicle_number}` : ""
                                      return `${sheet.ml_sheet_id} (${(sheet.shipment_ids || []).length} shipments)${routeLabel}${vehicleLabel}`
                                    }
                                    return (
                                      <div className="relative" ref={mlSheetDropdownRef}>
                                        {/* Trigger button */}
                                        <div
                                          onClick={() => setShowMlSheetDropdown((prev) => !prev)}
                                          className="w-full px-2.5 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white cursor-pointer flex items-center justify-between hover:border-emerald-400"
                                        >
                                          <span className={selectedSheet ? "text-gray-800" : "text-gray-400"}>
                                            {selectedSheet ? getSheetLabel(selectedSheet) : "Select today's ML sheet"}
                                          </span>
                                          <span className="text-gray-400 ml-2">▾</span>
                                        </div>

                                        {/* Dropdown panel */}
                                        {showMlSheetDropdown && (
                                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                                            {/* Search box — always visible */}
                                            <div className="p-2 border-b border-gray-200">
                                              <input
                                                type="text"
                                                autoFocus
                                                value={mlSheetSearch}
                                                onChange={(e) => setMlSheetSearch(e.target.value)}
                                                placeholder="Search by ID, location, vehicle..."
                                                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-xl focus:outline-none focus:border-emerald-500"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </div>

                                            {/* Results list */}
                                            <div className="max-h-52 overflow-y-auto">
                                              {filteredMlSheets.length === 0 ? (
                                                <div className="px-4 py-3 text-xs text-gray-400 text-center">No ML sheets found</div>
                                              ) : (
                                                filteredMlSheets.map((sheet) => (
                                                  <div
                                                    key={sheet.ml_sheet_id}
                                                    onClick={() => {
                                                      setSelectedExistingMlId(sheet.ml_sheet_id)
                                                      setShowMlSheetDropdown(false)
                                                      setMlSheetSearch("")
                                                    }}
                                                    className={`px-3 py-2 cursor-pointer text-xs border-b border-gray-100 last:border-b-0 hover:bg-emerald-50 ${
                                                      sheet.ml_sheet_id === selectedExistingMlId ? "bg-emerald-100 font-semibold" : ""
                                                    }`}
                                                  >
                                                    <div className="font-medium text-gray-800">{sheet.ml_sheet_id}</div>
                                                    <div className="text-gray-500 mt-0.5">
                                                      {(() => {
                                                        const fromLoc = sheet.from_locations?.[0]?.location || ""
                                                        const toLoc = sheet.to_locations?.[0]?.location || ""
                                                        const count = (sheet.shipment_ids || []).length
                                                        return `${count} shipment${count !== 1 ? "s" : ""}${fromLoc && toLoc ? ` · ${fromLoc} → ${toLoc}` : ""}${sheet.vehicle_number ? ` · ${sheet.vehicle_number}` : ""}`
                                                      })()}
                                                    </div>
                                                  </div>
                                                ))
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })()}

                                  {autoMlMode === "new" && (
                                    <div className="relative min-w-0" ref={vehicleDropdownRef}>
                                      <div
                                        onClick={() => autoMlOptions.vehicles.length > 0 && setShowVehicleDropdown((prev) => !prev)}
                                        className="w-full px-2.5 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white cursor-pointer flex items-center justify-between hover:border-emerald-400"
                                      >
                                        <span className={autoMlVehicleId ? "text-gray-800" : "text-gray-400"}>
                                          {autoMlVehicleId
                                            ? (() => {
                                                const v = autoMlOptions.vehicles.find((veh) => String(veh.id) === String(autoMlVehicleId))
                                                return v ? `${v.vehicle_number}${v.vehicle_name ? ` - ${v.vehicle_name}` : ""}` : "Select vehicle for this route"
                                              })()
                                            : autoMlOptions.vehicles.length > 0
                                            ? "Select vehicle for this route"
                                            : "No vehicles configured for this route"}
                                        </span>
                                        <span className="text-gray-400 ml-2">▾</span>
                                      </div>
                                      {showVehicleDropdown && autoMlOptions.vehicles.length > 0 && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                                          <div className="p-2 border-b border-gray-200">
                                            <input
                                              type="text"
                                              autoFocus
                                              value={vehicleSearch}
                                              onChange={(e) => setVehicleSearch(e.target.value)}
                                              placeholder="Search by number or name..."
                                              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-xl focus:outline-none focus:border-emerald-500"
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          </div>
                                          <div className="max-h-52 overflow-y-auto">
                                            <div
                                              onClick={() => { setAutoMlVehicleId(""); setShowVehicleDropdown(false); setVehicleSearch("") }}
                                              className="px-3 py-2 cursor-pointer text-xs text-gray-400 hover:bg-emerald-50 border-b border-gray-100"
                                            >
                                              Select vehicle for this route
                                            </div>
                                            {autoMlOptions.vehicles
                                              .filter((v) => {
                                                if (!vehicleSearch.trim()) return true
                                                const q = vehicleSearch.toLowerCase()
                                                return (
                                                  (v.vehicle_number || "").toLowerCase().includes(q) ||
                                                  (v.vehicle_name || "").toLowerCase().includes(q)
                                                )
                                              })
                                              .map((vehicle) => (
                                                <div
                                                  key={vehicle.id}
                                                  onClick={() => { setAutoMlVehicleId(vehicle.id); setShowVehicleDropdown(false); setVehicleSearch("") }}
                                                  className={`px-3 py-2 cursor-pointer text-xs border-b border-gray-100 last:border-b-0 hover:bg-emerald-50 ${
                                                    String(vehicle.id) === String(autoMlVehicleId) ? "bg-emerald-100 font-semibold" : ""
                                                  }`}
                                                >
                                                  <div className="font-medium text-gray-800">{vehicle.vehicle_number}</div>
                                                  {vehicle.vehicle_name && <div className="text-gray-500 mt-0.5">{vehicle.vehicle_name}</div>}
                                                </div>
                                              ))}
                                            {autoMlOptions.vehicles.filter((v) => {
                                              if (!vehicleSearch.trim()) return true
                                              const q = vehicleSearch.toLowerCase()
                                              return (v.vehicle_number || "").toLowerCase().includes(q) || (v.vehicle_name || "").toLowerCase().includes(q)
                                            }).length === 0 && (
                                              <div className="px-4 py-3 text-xs text-gray-400 text-center">No vehicles found</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {autoMlOverweight && (
                                        <div className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700 truncate" title={`Vehicle overweight. Shipment ${formatKg(autoMlShipmentWeight)} exceeds available today ${formatKg(autoMlAvailableKg)}.`}>
                                          Vehicle overweight — choose another vehicle
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  <div className="relative min-w-0" ref={agentDropdownRef}>
                                    <div
                                      onClick={() => autoMlOptions.agents.length > 0 && setShowAgentDropdown((prev) => !prev)}
                                      className={`w-full px-2.5 py-1 border rounded-lg text-xs sm:text-sm bg-white flex items-center justify-between ${
                                        autoMlOptions.agents.length > 0
                                          ? "border-gray-300 cursor-pointer hover:border-emerald-400"
                                          : "border-gray-200 cursor-default"
                                      }`}
                                    >
                                      <span className={autoMlAgentUserId ? "text-gray-800" : "text-gray-400"}>
                                        {autoMlAgentUserId
                                          ? (() => {
                                              const a = autoMlOptions.agents.find((ag) => ag.user_id === autoMlAgentUserId)
                                              return a ? `${a.name}${a.city ? ` - ${a.city}` : ""} (${a.user_id})` : "Select receiver location agent"
                                            })()
                                          : autoMlOptions.agents.length > 0
                                          ? autoMlOptions.agent_required
                                            ? "Select receiver location agent"
                                            : "Select receiver location agent (optional)"
                                          : autoMlOptions.agent_required
                                          ? "No agents found for sender location"
                                          : "No agent selected (optional)"}
                                      </span>
                                      {autoMlOptions.agents.length > 0 && <span className="text-gray-400 ml-2">▾</span>}
                                    </div>
                                    {showAgentDropdown && autoMlOptions.agents.length > 0 && (
                                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                                        <div className="p-2 border-b border-gray-200">
                                          <input
                                            type="text"
                                            autoFocus
                                            value={agentSearch}
                                            onChange={(e) => setAgentSearch(e.target.value)}
                                            placeholder="Search by name, ID or city..."
                                            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-xl focus:outline-none focus:border-emerald-500"
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                        <div className="max-h-52 overflow-y-auto">
                                          <div
                                            onClick={() => { setAutoMlAgentUserId(""); setShowAgentDropdown(false); setAgentSearch("") }}
                                            className="px-3 py-2 cursor-pointer text-xs text-gray-400 hover:bg-emerald-50 border-b border-gray-100"
                                          >
                                            Select receiver location agent
                                          </div>
                                          {autoMlOptions.agents
                                            .filter((a) => {
                                              if (!agentSearch.trim()) return true
                                              const q = agentSearch.toLowerCase()
                                              return (
                                                (a.name || "").toLowerCase().includes(q) ||
                                                (a.user_id || "").toLowerCase().includes(q) ||
                                                (a.city || "").toLowerCase().includes(q)
                                              )
                                            })
                                            .map((agent) => (
                                              <div
                                                key={agent.user_id}
                                                onClick={() => { setAutoMlAgentUserId(agent.user_id); setShowAgentDropdown(false); setAgentSearch("") }}
                                                className={`px-3 py-2 cursor-pointer text-xs border-b border-gray-100 last:border-b-0 hover:bg-emerald-50 ${
                                                  agent.user_id === autoMlAgentUserId ? "bg-emerald-100 font-semibold" : ""
                                                }`}
                                              >
                                                <div className="font-medium text-gray-800">{agent.name}</div>
                                                <div className="text-gray-500 mt-0.5 flex flex-wrap items-center gap-1">
                                                  {agent.scope_label && (
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                      agent.scope === 'EXTERNAL' ? 'bg-indigo-100 text-indigo-700' :
                                                      agent.scope === 'PARTY' ? 'bg-purple-100 text-purple-700' :
                                                      'bg-gray-100 text-gray-600'
                                                    }`}>
                                                      {agent.scope_label}
                                                    </span>
                                                  )}
                                                  <span>{agent.city ? `${agent.city} · ` : ''}{agent.user_id}</span>
                                                  {agent.scope === 'EXTERNAL' && agent.home_company_name && (
                                                    <span className="text-indigo-600">({agent.home_company_name})</span>
                                                  )}
                                                  {agent.scope === 'PARTY' && agent.party_name && (
                                                    <span className="text-purple-600">({agent.party_name})</span>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          {autoMlOptions.agents.filter((a) => {
                                            if (!agentSearch.trim()) return true
                                            const q = agentSearch.toLowerCase()
                                            return (a.name || "").toLowerCase().includes(q) || (a.user_id || "").toLowerCase().includes(q) || (a.city || "").toLowerCase().includes(q)
                                          }).length === 0 && (
                                            <div className="px-4 py-3 text-xs text-gray-400 text-center">No agents found</div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {canSelectCrossingHubs && (
                                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-1.5 min-w-0">
                                      <div className="relative min-w-0">
                                        <div
                                          onClick={() => setShowAutoMlCrossing((prev) => !prev)}
                                          className="w-full px-2.5 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white cursor-pointer flex items-center justify-between hover:border-emerald-400"
                                        >
                                          <span className={autoMlCrossingHubIds.length ? "text-gray-800" : "text-gray-400"}>
                                            {autoMlCrossingHubIds.length
                                              ? `Crossing hubs (${autoMlCrossingHubIds.length})`
                                              : "Crossing location hubs (optional)"}
                                          </span>
                                          <span className="text-gray-400 ml-2">▾</span>
                                        </div>
                                        {showAutoMlCrossing && (
                                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                                            <div className="p-2 border-b border-gray-200">
                                              <input
                                                type="text"
                                                autoFocus
                                                value={autoMlCrossingSearch}
                                                onChange={(e) => setAutoMlCrossingSearch(e.target.value)}
                                                placeholder="Search hubs..."
                                                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-xl focus:outline-none focus:border-emerald-500"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </div>
                                            <div className="max-h-52 overflow-y-auto">
                                              {autoMlHubChoices
                                                .filter((hub) => {
                                                  if (!autoMlCrossingSearch.trim()) return true
                                                  const q = autoMlCrossingSearch.toLowerCase()
                                                  return (formatHubBranchLabel(hub) || hub.location || "").toLowerCase().includes(q)
                                                })
                                                .map((hub) => {
                                                  const selected = autoMlCrossingHubIds.some((id) => String(id) === String(hub.id))
                                                  return (
                                                    <label key={hub.id} className="px-3 py-2 text-xs border-b border-gray-100 last:border-b-0 hover:bg-emerald-50 flex items-start gap-2 cursor-pointer">
                                                      <input
                                                        type="checkbox"
                                                        checked={selected}
                                                        onChange={() => {
                                                          setAutoMlCrossingHubIds((prev) => {
                                                            const next = selected
                                                              ? prev.filter((id) => String(id) !== String(hub.id))
                                                              : [...prev, hub.id]
                                                            if (!next.length) setAutoMlCrossingDestIds([])
                                                            return next
                                                          })
                                                        }}
                                                        className="mt-0.5"
                                                      />
                                                      <span>{formatHubBranchLabel(hub) || hub.location}</span>
                                                    </label>
                                                  )
                                                })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      {autoMlCrossingHubIds.length > 0 && (
                                        <div className="relative min-w-0">
                                          <div
                                            onClick={() => setShowAutoMlCrossingDest((prev) => !prev)}
                                            className="w-full px-2.5 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white cursor-pointer flex items-center justify-between hover:border-emerald-400"
                                          >
                                            <span className={autoMlCrossingDestIds.length ? "text-gray-800" : "text-gray-400"}>
                                              {autoMlCrossingDestIds.length
                                                ? "Receiver location will be crossed"
                                                : "Locations to cross"}
                                            </span>
                                            <span className="text-gray-400 ml-2">▾</span>
                                          </div>
                                          {showAutoMlCrossingDest && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                                              <div className="max-h-52 overflow-y-auto">
                                                {(() => {
                                                  const receiverId = receiverHubId || formData.receiver_location_hub_id
                                                  const hub = autoMlHubChoices.find((item) => String(item.id) === String(receiverId))
                                                  if (!hub) {
                                                    return <div className="px-3 py-2 text-xs text-gray-400">Select the receiver location first</div>
                                                  }
                                                  const selected = autoMlCrossingDestIds.some((id) => String(id) === String(hub.id))
                                                  return (
                                                    <label className="px-3 py-2 text-xs hover:bg-emerald-50 flex items-start gap-2 cursor-pointer">
                                                      <input
                                                        type="checkbox"
                                                        checked={selected}
                                                        onChange={() => {
                                                          setAutoMlCrossingDestIds(selected ? [] : [hub.id])
                                                        }}
                                                        className="mt-0.5"
                                                      />
                                                      <span>{formatHubBranchLabel(hub) || hub.location}</span>
                                                    </label>
                                                  )
                                                })()}
                                              </div>
                                            </div>
                                          )}
                                          <p className="mt-1 text-[11px] text-gray-500">
                                            Only this location's shipment shows on the crossing page. The crossing hub does not see this dispatch.
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                            </div>
                          )}
                            </div>
                          )}

                          {!autoMlEnabled && !mlCreationBlock.blocked && (
                            <div className="mt-1.5 flex items-center bg-white px-2.5 py-1 rounded-lg border border-gray-200">
                              <span className="text-xs text-gray-500 italic truncate">ML sheet will not be created automatically</span>
                            </div>
                          )}
                    </div>
                  </div>
                  )}

                <div className="bg-white rounded-xl shadow-md p-3 sm:p-4 mb-2 min-w-0 shrink-0 max-md:order-1">
                  {/* Section Header Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 pb-2 border-b-2 border-gray-200">
                    <h2 className="text-sm sm:text-base font-semibold text-gray-700">Destination & Documents</h2>
                    <div className="flex flex-row gap-2 shrink-0 flex-wrap items-center">
                        <Button variant="secondary" type="button" tabIndex={-1} onClick={() => setShowEwayBillModal(true)}>
                          Add E-way Unique ID
                          {ewaybillFeatureOn && (Number.parseFloat(formData.shipment_value) || 0) > EWAY_BILL_MANDATORY_VALUE ? (
                            <span className="text-red-500"> *</span>
                          ) : null}
                        </Button>
                        <Button
                          variant={submittedEwayBills.length > 0 ? "primary" : "secondary"}
                          type="button"
                          tabIndex={-1}
                          onClick={handleViewEwayBills}
                        >
                          View Eway Bill {submittedEwayBills.length > 0 && `(${submittedEwayBills.length})`}
                        </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-3">
                    {/* From Destination */}
                    <div className="flex flex-col relative" ref={senderRef}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        From Destination <span className="text-red-500">*</span>
                      </label>
                      <div
                        onClick={() => { if (!senderLocationLocked) { setShowSenderDropdown(true); setSenderHighlightIndex(-1); } }}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-xl cursor-pointer select-none ${
                          formData.sender_location 
                            ? 'border-gray-300' 
                            : 'border-red-300'
                        } ${senderLocationLocked ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-orange-400'}`}
                      >
                        {formData.sender_location ? (
                          <span className="text-gray-800">{senderLocationDisplay}</span>
                        ) : (
                          <span className="text-gray-400">Search and select location</span>
                        )}
                      </div>
                      {showSenderDropdown && (
                        <div className="absolute z-10 w-full mt-1 top-full bg-white border border-gray-300 rounded-xl shadow-lg">
                          <div className="p-2 border-b border-gray-200">
                            <input
                              type="text"
                              autoFocus
                              value={senderLocationSearch}
                              onChange={(e) => { setSenderLocationSearch(e.target.value); setSenderHighlightIndex(-1); }}
                              onKeyDown={(e) =>
                                handleLocationDropdownKeyDown(e, {
                                  isOpen: showSenderDropdown,
                                  options: filteredSenderLocations,
                                  highlightIndex: senderHighlightIndex,
                                  setHighlightIndex: setSenderHighlightIndex,
                                  onClose: () => { setShowSenderDropdown(false); setSenderLocationSearch(""); },
                                  onSelect: (loc) => {
                                    const label = formatHubBranchLabel(loc)
                                    const senderId = getLocationHubPrimaryId(loc)
                                    if (senderId === formData.receiver_location_hub_id && formData.receiver_location_hub_id !== "") {
                                      Alert.error("Sender and Receiver locations cannot be the same")
                                      return
                                    }
                                    setFormData({
                                      ...formData,
                                      sender_location: label,
                                      sender_location_hub_id: senderId,
                                    })
                                    setSenderLocationSearch("")
                                    setShowSenderDropdown(false)
                                    setSenderHighlightIndex(-1)
                                    updateGcPrefix(
                                      consignerLocations,
                                      senderId,
                                      formData.receiver_location_hub_id,
                                      label,
                                      formData.receiver_location,
                                    )
                                  },
                                })
                              }
                              placeholder="Type to search..."
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            {filteredSenderLocations.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-400 text-center">No locations found</div>
                            ) : filteredSenderLocations.map((loc, idx) => (
                              <div
                                key={loc.hub_id}
                                onMouseEnter={() => setSenderHighlightIndex(idx)}
                                onClick={() => {
                                  const label = formatHubBranchLabel(loc)
                                  const senderId = getLocationHubPrimaryId(loc)
                                  if (senderId === formData.receiver_location_hub_id && formData.receiver_location_hub_id !== "") {
                                    Alert.error("Sender and Receiver locations cannot be the same")
                                    return
                                  }
                                  setFormData({
                                    ...formData,
                                    sender_location: label,
                                    sender_location_hub_id: senderId,
                                  })
                                  setSenderLocationSearch("")
                                  setShowSenderDropdown(false)
                                  setSenderHighlightIndex(-1)
                                  updateGcPrefix(
                                    consignerLocations,
                                    senderId,
                                    formData.receiver_location_hub_id,
                                    label,
                                    formData.receiver_location,
                                  )
                                }}
                                className={`px-3 sm:px-4 py-2 cursor-pointer text-sm sm:text-base border-b border-gray-100 last:border-b-0 ${
                                  idx === senderHighlightIndex ? "bg-orange-50" : "hover:bg-orange-50"
                                }`}
                              >
                                {formatLocationDropdownLabel(loc)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* To Destination */}
                    <div className="flex flex-col relative" ref={receiverRef}>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        To Destination <span className="text-red-500">*</span>
                      </label>
                      <div
                        onClick={() => { setShowReceiverDropdown(true); setReceiverHighlightIndex(-1); }}
                        className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border rounded-xl cursor-pointer select-none ${
                          formData.receiver_location 
                            ? 'border-gray-300 bg-white hover:border-orange-400' 
                            : 'border-red-300 bg-white hover:border-red-400'
                        }`}
                      >
                        {formData.receiver_location ? (
                          <span className="text-gray-800">{receiverLocationDisplay}</span>
                        ) : (
                          <span className="text-gray-400">Select Location</span>
                        )}
                      </div>
                      {showReceiverDropdown && (
                        <div className="absolute z-50 w-full mt-1 top-full bg-white border border-gray-300 rounded-xl shadow-lg">
                          <div className="p-2 border-b border-gray-200">
                            <input
                              type="text"
                              autoFocus
                              value={receiverLocationSearch}
                              onChange={(e) => { setReceiverLocationSearch(e.target.value); setReceiverHighlightIndex(-1); }}
                              onKeyDown={(e) =>
                                handleLocationDropdownKeyDown(e, {
                                  isOpen: showReceiverDropdown,
                                  options: filteredReceiverLocations,
                                  highlightIndex: receiverHighlightIndex,
                                  setHighlightIndex: setReceiverHighlightIndex,
                                  onClose: () => { setShowReceiverDropdown(false); setReceiverLocationSearch(""); },
                                  onSelect: (loc) => {
                                    const label = formatHubBranchLabel(loc)
                                    const receiverId = getLocationHubPrimaryId(loc)
                                    if (receiverId === formData.sender_location_hub_id && formData.sender_location_hub_id !== "") {
                                      Alert.error("Sender and Receiver locations cannot be the same")
                                      return
                                    }
                                    const hubRate = rateFromHub(loc)
                                    setFormData({
                                      ...formData,
                                      receiver_location: label,
                                      receiver_location_hub_id: receiverId,
                                      ...(hubRate !== null ? { rate_per_kg: hubRate } : {}),
                                    })
                                    setReceiverLocationSearch("")
                                    setShowReceiverDropdown(false)
                                    setReceiverHighlightIndex(-1)
                                    updateGcPrefix(
                                      consignerLocations,
                                      formData.sender_location_hub_id,
                                      receiverId,
                                      formData.sender_location,
                                      label,
                                    )
                                  },
                                })
                              }
                              placeholder="Type to search..."
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            {filteredReceiverLocations.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-400 text-center">No locations found</div>
                            ) : filteredReceiverLocations.map((loc, idx) => (
                              <div
                                key={loc.hub_id}
                                onMouseEnter={() => setReceiverHighlightIndex(idx)}
                                onClick={() => {
                                  const label = formatHubBranchLabel(loc)
                                  const receiverId = getLocationHubPrimaryId(loc)
                                  if (receiverId === formData.sender_location_hub_id && formData.sender_location_hub_id !== "") {
                                    Alert.error("Sender and Receiver locations cannot be the same")
                                    return
                                  }
                                  const hubRate = rateFromHub(loc)
                                  setFormData({
                                    ...formData,
                                    receiver_location: label,
                                    receiver_location_hub_id: receiverId,
                                    ...(hubRate !== null ? { rate_per_kg: hubRate } : {}),
                                  })
                                  setReceiverLocationSearch("")
                                  setShowReceiverDropdown(false)
                                  setReceiverHighlightIndex(-1)
                                  updateGcPrefix(
                                    consignerLocations,
                                    formData.sender_location_hub_id,
                                    receiverId,
                                    formData.sender_location,
                                    label,
                                  )
                                }}
                                className={`px-3 sm:px-4 py-2 cursor-pointer text-sm sm:text-base border-b border-gray-100 last:border-b-0 ${
                                  idx === receiverHighlightIndex ? "bg-orange-50" : "hover:bg-orange-50"
                                }`}
                              >
                                {formatLocationDropdownLabel(loc)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col min-w-0">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Booking Date
                      </label>
                      <input
                        type="date"
                        name="booking_date"
                        value={formData.booking_date}
                        readOnly
                        tabIndex={-1}
                        onChange={handleChange}
                        onKeyDown={(e) => e.preventDefault()}
                        max={getLocalDateString()}
                        className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-50 cursor-not-allowed pointer-events-none focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
                </div>

                <div className={`space-y-3 md:space-y-3 min-w-0 flex-1 min-h-0 ${isMobileLayout ? (mobileStep === 4 ? "flex flex-col overflow-visible min-h-0 flex-1" : "flex flex-col overflow-hidden min-h-0 flex-1") : "md:overflow-visible"} ${isMobileLayout && mobileStep === 0 ? "hidden" : ""}`}>
                  {/* Consignor (left) | Consignee (right) — stacked on mobile wizard */}
                  <div className={`${isMobileLayout ? "contents" : `grid grid-cols-1 md:grid-cols-2 gap-3 relative ${isConsigneeSuggestionsOpen ? "z-[60]" : ""}`}`}>
                      <div className={`rounded-xl pb-2 sm:pb-3 bg-white p-4 md:p-4 ${mobileStepPanelClass(1)} md:!block md:overflow-visible ${isConsignorSuggestionsOpen ? "relative z-50" : ""}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                          <h2 className="text-base sm:text-md font-semibold text-gray-700">
                            Consignor Information
                          </h2>

                          <div className="flex items-center gap-3 shrink-0">
                            {hubAllowsCredit ? (
                            <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                id="useShipmentClient"
                                checked={useShipmentClient}
                                onChange={(e) => handleUseClientChange(e.target.checked)}
                                className="w-4 h-4 text-orange-600 focus:ring-blue-400 border-gray-300 rounded"
                              />
                              Select from Credit Clients
                            </label>
                            ) : null}
                          </div>
                        </div>
                        
                        <div className="space-y-0">
                          <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? '' : 'sm:grid-cols-2'}`}>

                            {/* Contact Number */}
                            <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                Contact Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="sender_contact"
                                value={formData.sender_contact}
                                onChange={(e) => {
                                  handleChange(e)
                                  setSenderSearchQuery(e.target.value)
                                  setSenderSuggestionHighlightIndex(-1)
                                  if (!useShipmentClient) {
                                    fetchSenderSuggestions(e.target.value)
                                  }
                                }}
                                onFocus={() => {
                                  if (!useShipmentClient && String(formData.sender_contact || "").replace(/\D/g, "").length >= 10) {
                                    setShowSenderSuggestions(true)
                                    setSenderSuggestionHighlightIndex(-1)
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => { setShowSenderSuggestions(false); setSenderSuggestionHighlightIndex(-1) }, 200)
                                }}
                                onKeyDown={(e) =>
                                  handleSuggestionDropdownKeyDown(e, {
                                    isOpen: showSenderSuggestions && !senderSuggestionsLoading && senderSuggestions.length > 0,
                                    options: senderSuggestions,
                                    highlightIndex: senderSuggestionHighlightIndex,
                                    setHighlightIndex: setSenderSuggestionHighlightIndex,
                                    onSelect: handleSenderSuggestionSelect,
                                    onClose: () => { setShowSenderSuggestions(false); setSenderSuggestionHighlightIndex(-1) },
                                    onEnterFallback: handleEnterAsTab,
                                  })
                                }
                                placeholder="10 digit mobile number"
                                required
                                autoComplete="off"
                                disabled={useShipmentClient}
                                maxLength={10}
                                className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.sender_contact ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100`}
                              />
                              <ValidationMessage error={errors.sender_contact} />
                              {!useShipmentClient && String(formData.sender_contact || "").replace(/\D/g, "").length === 10 && !senderContactClient && hubAllowsCredit && (
                                <p className="mt-1 text-[11px] text-amber-700">This contact is not a credit customer. Credit is disabled.</p>
                              )}
                              {showSenderSuggestions && (senderSuggestions.length > 0 || senderSuggestionsLoading) && !useShipmentClient && (
                                <div className={getSuggestionListClass(isMobileLayout)}>
                                  {senderSuggestionsLoading && (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
                                  )}
                                  {!senderSuggestionsLoading && senderSuggestions.length === 0 && (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">No suggestions found</div>
                                  )}
                                  {Array.isArray(senderSuggestions) && senderSuggestions.map((suggestion, index) => (
                                    <div
                                      key={index}
                                      ref={(node) => {
                                        if (index === senderSuggestionHighlightIndex) scrollHighlightedSuggestionIntoView(node)
                                      }}
                                      onMouseEnter={() => setSenderSuggestionHighlightIndex(index)}
                                      onClick={() => handleSenderSuggestionSelect(suggestion)}
                                      className={getSuggestionItemClass(index === senderSuggestionHighlightIndex)}
                                    >
                                      <div className="font-medium text-gray-800">{suggestion.name}</div>
                                      <div className="text-xs text-gray-500">{suggestion.contact}</div>
                                      {suggestion.address && <div className="text-xs text-gray-400 truncate">{suggestion.address}</div>}
                                      {suggestion.paired_receiver?.name && (
                                        <div className="text-xs text-blue-600 truncate mt-0.5">
                                          → To: {suggestion.paired_receiver.name}
                                          {suggestion.paired_receiver.contact ? ` (${suggestion.paired_receiver.contact})` : ""}
                                          {suggestion.paired_receiver.address ? `, ${suggestion.paired_receiver.address}` : ""}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Consignor Name */}
                            <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                Consignor Name <span className="text-red-500">*</span>
                              </label>

                              {useShipmentClient ? (
                                <>
                                  <input
                                    type="text"
                                    value={
                                      selectedClient
                                        ? `${selectedClient.full_name} (${selectedClient.company_name})`
                                        : clientSearch
                                    }
                                    onChange={(e) => {
                                      setClientSearch(e.target.value)
                                      setSelectedClient(null)
                                      setShowClientDropdown(true)
                                      setClientHighlightIndex(-1)
                                    }}
                                    onFocus={() => {
                                      setShowClientDropdown(true)
                                      setClientHighlightIndex(-1)
                                    }}
                                    onBlur={() => setTimeout(() => { setShowClientDropdown(false); setClientHighlightIndex(-1) }, 200)}
                                    onKeyDown={(e) =>
                                      handleSuggestionDropdownKeyDown(e, {
                                        isOpen: showClientDropdown,
                                        options: filteredClients,
                                        highlightIndex: clientHighlightIndex,
                                        setHighlightIndex: setClientHighlightIndex,
                                        onSelect: handleClientSelect,
                                        onClose: () => { setShowClientDropdown(false); setClientHighlightIndex(-1) },
                                        onEnterFallback: handleEnterAsTab,
                                      })
                                    }
                                    className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                                    required
                                  />
                                  {showClientDropdown && filteredClients.length > 0 && (
                                    <div className={getSuggestionListClass(isMobileLayout)}>
                                      {filteredClients.map((client, index) => (
                                        <div
                                          key={index}
                                          ref={(node) => {
                                            if (index === clientHighlightIndex) scrollHighlightedSuggestionIntoView(node)
                                          }}
                                          onMouseEnter={() => setClientHighlightIndex(index)}
                                          onClick={() => handleClientSelect(client)}
                                          className={getSuggestionItemClass(index === clientHighlightIndex)}
                                        >
                                          <div className="font-medium text-gray-800">{client.full_name}</div>
                                          <div className="text-xs text-gray-500">{client.contact}</div>
                                          {client.address && <div className="text-xs text-gray-400 truncate">{client.address}</div>}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    name="sender_name"
                                    value={formData.sender_name}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    placeholder="Consignor name"
                                    required
                                    autoComplete="off"
                                    className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.sender_name ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                                  />
                                  <ValidationMessage error={errors.sender_name} />
                                </>
                              )}
                            </div>

                          </div>

                            <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? '' : 'sm:grid-cols-3'}`}>
                              <GstVerificationField
                                name="sender_gst"
                                value={formData.sender_gst}
                                onChange={handleChange}
                                onStatusChange={setSenderGstStatus}
                                error={errors.sender_gst}
                                enabled={gstVerificationOn}
                                disabled={useShipmentClient && selectedClient}
                              />
                              <div className="flex flex-col min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  name="sender_email"
                                  placeholder="Email address"
                                  value={formData.sender_email}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  disabled={useShipmentClient && selectedClient}
                                  className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.sender_email ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100`}
                                />
                                <ValidationMessage error={errors.sender_email} />
                              </div>

                              <div className="flex flex-col min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Address
                                </label>
                                <input
                                  type="text"
                                  name="sender_address"
                                  placeholder="Full Address"
                                  value={formData.sender_address}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  disabled={useShipmentClient && selectedClient}
                                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100"
                                />
                              </div>
                            </div>

                        </div>
                      </div>

                      {/* Consignee Information */}
                      <div className={`rounded-xl pb-2 sm:pb-3 bg-white p-4 md:p-4 ${mobileStepPanelClass(2)} md:!block md:overflow-visible ${isConsigneeSuggestionsOpen ? "relative z-[70]" : ""}`}>
                        <div className="flex items-center justify-between mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                          <h2 className="text-base sm:text-md font-semibold text-gray-700">Consignee Information</h2>
                        </div>

                        <div className="space-y-0">
                          <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? '' : 'sm:grid-cols-2'}`}>

                            {/* Contact Number */}
                            <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                Contact Number <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="receiver_contact"
                                placeholder="10 digit mobile number"
                                value={formData.receiver_contact}
                                onChange={(e) => {
                                  handleChange(e)
                                  setReceiverSearchQuery(e.target.value)
                                  setReceiverSuggestionHighlightIndex(-1)
                                  fetchReceiverSuggestions(e.target.value)
                                }}
                                onFocus={() => {
                                  if (String(formData.receiver_contact || "").replace(/\D/g, "").length >= 10) {
                                    setShowReceiverSuggestions(true)
                                    setReceiverSuggestionHighlightIndex(-1)
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => { setShowReceiverSuggestions(false); setReceiverSuggestionHighlightIndex(-1) }, 200)
                                }}
                                onKeyDown={(e) =>
                                  handleSuggestionDropdownKeyDown(e, {
                                    isOpen: showReceiverSuggestions && !receiverSuggestionsLoading && receiverSuggestions.length > 0,
                                    options: receiverSuggestions,
                                    highlightIndex: receiverSuggestionHighlightIndex,
                                    setHighlightIndex: setReceiverSuggestionHighlightIndex,
                                    onSelect: handleReceiverSuggestionSelect,
                                    onClose: () => { setShowReceiverSuggestions(false); setReceiverSuggestionHighlightIndex(-1) },
                                    onEnterFallback: handleEnterAsTab,
                                  })
                                }
                                required
                                autoComplete="off"
                                maxLength={10}
                                className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.receiver_contact ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                              />
                              <ValidationMessage error={errors.receiver_contact} />
                              {showReceiverSuggestions && (receiverSuggestions.length > 0 || receiverSuggestionsLoading) && (
                                <div className={getSuggestionListClass(isMobileLayout)}>
                                  {receiverSuggestionsLoading && (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading...</div>
                                  )}
                                  {!receiverSuggestionsLoading && receiverSuggestions.length === 0 && (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">No suggestions found</div>
                                  )}
                                  {Array.isArray(receiverSuggestions) && receiverSuggestions.map((suggestion, index) => (
                                    <div
                                      key={index}
                                      ref={(node) => {
                                        if (index === receiverSuggestionHighlightIndex) scrollHighlightedSuggestionIntoView(node)
                                      }}
                                      onMouseEnter={() => setReceiverSuggestionHighlightIndex(index)}
                                      onClick={() => handleReceiverSuggestionSelect(suggestion)}
                                      className={getSuggestionItemClass(index === receiverSuggestionHighlightIndex)}
                                    >
                                      <div className="font-medium text-gray-800">{suggestion.name}</div>
                                      <div className="text-xs text-gray-500">{suggestion.contact}</div>
                                      {suggestion.address && <div className="text-xs text-gray-400 truncate">{suggestion.address}</div>}
                                      {suggestion.paired_sender?.name && (
                                        <div className="text-xs text-blue-600 truncate mt-0.5">
                                          ← From: {suggestion.paired_sender.name}
                                          {suggestion.paired_sender.contact ? ` (${suggestion.paired_sender.contact})` : ""}
                                          {suggestion.paired_sender.address ? `, ${suggestion.paired_sender.address}` : ""}
                                        </div>
                                      )}
                                    </div>
                                  ))} 
                                </div>
                              )}
                            </div>

                            {/* Consignee Name */}
                            <div className={`flex flex-col relative ${isMobileLayout ? "" : "z-30"}`}>
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                Consignee Name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                name="receiver_name"
                                placeholder="Consignee name"
                                value={formData.receiver_name}
                                onChange={handleChange}
                                onKeyDown={handleEnterAsTab}
                                required
                                autoComplete="off"
                                className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.receiver_name ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                              />
                              <ValidationMessage error={errors.receiver_name} />
                            </div>

                          </div>

                            <div className={`grid grid-cols-1 gap-3 sm:gap-3 mb-3 ${isMobileLayout ? '' : 'sm:grid-cols-3'}`}>
                              <GstVerificationField
                                name="receiver_gst"
                                value={formData.receiver_gst}
                                onChange={handleChange}
                                onKeyDown={handleEnterAsTab}
                                onStatusChange={setReceiverGstStatus}
                                error={errors.receiver_gst}
                                enabled={gstVerificationOn}
                              />
                              <div className="flex flex-col min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  name="receiver_email"
                                  placeholder="Email address"
                                  value={formData.receiver_email}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border ${errors.receiver_email ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200`}
                                />
                                <ValidationMessage error={errors.receiver_email} />
                              </div>

                              <div className="flex flex-col min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Address
                                </label>
                                <input
                                  type="text"
                                  name="receiver_address"
                                  placeholder="Full Address"
                                  value={formData.receiver_address}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                                />
                              </div>
                            </div>

                        </div>
                      </div>
                  </div>

                  {/* Shipment Details (left) | Shipment Charges (right) — stacked on mobile wizard */}
                  <div className={`${isMobileLayout ? "contents" : "grid grid-cols-1 md:grid-cols-2 gap-3 md:min-h-0"}`}>
                    <div className={`bg-white rounded-xl p-3 sm:p-4 mb-2 md:overflow-y-auto md:min-h-0 ${mobileStepPanelClass(3)} md:!block`}>
                      <div className="flex items-center justify-between gap-2 mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                        <h2 className="text-base sm:text-lsm font-semibold text-gray-700 min-w-0">
                          Shipment Details
                        </h2>
                        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowParcelImages(!showParcelImages)}
                            className="flex items-center justify-center p-1 rounded hover:bg-gray-100 transition"
                            aria-label="Toggle parcel images"
                          >
                            {showParcelImages ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-0">
                        {!packageLayoutReady ? (
                          <div className="h-24 rounded-xl bg-gray-50 border border-gray-100 animate-pulse mb-3" />
                        ) : isCargoMode ? (
                          <div className="min-w-0 mb-3">
                            <PackageDimensionForm
                              parcels={parcels}
                              onChange={setParcels}
                              title="Package Dimensions"
                              badge="Cargo"
                              ratePerKg={formData.rate_per_kg}
                            />
                          </div>
                        ) : (
                          <BusArticlePackagesForm
                            articles={parcels}
                            onChange={handleBusArticlesChange}
                            freightByWeight={busFreightByWeight}
                            title="Article Details"
                            badge="Bus"
                          />
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          <div className="flex flex-col min-w-0">
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Customer Invoice Number
                            </label>
                            <input
                              type="text"
                              name="customer_invoice_number"
                              value={formData.customer_invoice_number}
                              onChange={handleChange}
                              onKeyDown={handleEnterAsTab}
                              placeholder="Consignor invoice number"
                              className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                            />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Customer Value of the Goods
                            </label>
                            <input
                              type="text"
                              name="shipment_value"
                              value={formData.shipment_value}
                              onChange={handleChange}
                              onKeyDown={handleEnterAsTab}
                              placeholder="Value of goods"
                              className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                            />
                          </div>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4 mb-4">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div>
                              <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-2 sm:mb-3">
                                Photos &amp; Videos
                              </h3>
                              <div ref={parcelImagesRef}>
                                <ShipmentMediaUploader
                                  files={shipmentMediaFiles}
                                  onFilesChange={setShipmentMediaFiles}
                                  compact
                                  dropzone={isMobileLayout}
                                />
                              </div>
                            </div>
                            <div className="flex flex-col">
                              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                Remarks / Note
                              </label>
                              <textarea
                                name="parcel_detail"
                                value={formData.parcel_detail}
                                onChange={handleChange}
                                rows="4"
                                placeholder="Any note or remark for this shipment"
                                className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                              />
                            </div>
                          </div>
                        </div>

                        {showParcelImages && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 border p-4 mb-4">
                            {!isCargoMode && !busFreightByWeight && (
                              <div className="flex flex-col">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Weight (kg)
                                </label>
                                <input
                                  type="text"
                                  name="weight"
                                  value={formData.weight}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                                />
                              </div>
                            )}

                          </div>
                        )}

                      </div>
                    </div>

                    <div className={`bg-white rounded-xl p-3 sm:p-4 mb-2 md:overflow-y-auto md:min-h-0 overflow-visible ${isMobileLayout ? (mobileStep === 4 ? "block w-full min-w-0 overflow-visible pb-28" : "hidden") : ""} md:!block`}>
                          <div className="flex items-center justify-between mb-2 sm:mb-2 pb-1.5 border-b-2 border-gray-200">
                            <h2 className="text-base sm:text-lsm font-semibold text-gray-700">Shipment Charges</h2>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowGSTDetails(!showGSTDetails)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded transition"
                              >
                                GST
                                {showGSTDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                              </button>
                              <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => setShowAdditionalCharges(!showAdditionalCharges)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition"
                              >
                                Additional
                                {showAdditionalCharges ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                              </button>
                            </div>
                          </div>

                          {/* GST / Additional — expand inline below toggles (no page scroll jump) */}
                          {showGSTDetails && (
                            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 p-3 sm:p-4 border border-purple-200 rounded-xl bg-purple-50/40 mb-3">

                              {/* SGST % */}
                              <div className="flex flex-col">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  SGST %
                                </label>
                                <input
                                  type="text"
                                  name="sgst_percentage"
                                  value={formData.sgst_percentage}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  disabled={rcmActive || billingGstIsInvalid()}
                                  readOnly={rcmActive || billingGstIsInvalid()}
                                  className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || billingGstIsInvalid() ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                />
                              </div>

                              {/* CGST % */}
                              <div className="flex flex-col">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  CGST %
                                </label>
                                <input
                                  type="text"
                                  name="cgst_percentage"
                                  value={formData.cgst_percentage}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  disabled={rcmActive || billingGstIsInvalid()}
                                  readOnly={rcmActive || billingGstIsInvalid()}
                                  className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || billingGstIsInvalid() ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                />
                              </div>

                              {/* IGST % */}
                              <div className="flex flex-col">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  IGST %
                                </label>
                                <input
                                  type="text"
                                  name="igst_percentage"
                                  value={formData.igst_percentage}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  disabled={rcmActive || billingGstIsInvalid()}
                                  readOnly={rcmActive || billingGstIsInvalid()}
                                  className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${rcmActive || billingGstIsInvalid() ? 'bg-gray-100 cursor-not-allowed text-gray-700' : 'bg-white'}`}
                                />
                              </div>

                              {/* SGST ₹ */}
                              <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().sgst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}>
                                <p className="text-xs text-gray-600 font-semibold">SGST</p>
                                <p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().sgst}</p>
                              </div>

                              {/* CGST ₹ */}
                              <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().cgst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}>
                                <p className="text-xs text-gray-600 font-semibold">CGST</p>
                                <p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().cgst}</p>
                              </div>

                              {/* IGST ₹ */}
                              <div className={`p-3 rounded-xl ${Number(calculateGSTAmounts().igst) > 0 ? 'bg-purple-50' : 'bg-gray-50 opacity-60'}`}>
                                <p className="text-xs text-gray-600 font-semibold">IGST</p>
                                <p className="text-lg font-bold text-purple-600">₹{calculateGSTAmounts().igst}</p>
                              </div>

                            </div>
                          )}

                          {showAdditionalCharges && (
                            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 p-3 sm:p-4 border border-blue-200 rounded-xl bg-blue-50/40 mb-3">
                              {!canEditAdditionalCharges && (
                                <p className="col-span-full text-[11px] text-amber-700 font-medium">
                                  Loading and Docket Fee are set by Admin. Door Pickup and Door Delivery can be entered by agents.
                                </p>
                              )}
                              {chargeDefaults.insurance?.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Insurance Charge
                                    {chargeDefaults.insurance?.mode === 'percent' && (
                                      <span className="text-gray-400 font-normal">
                                        {` (${chargeDefaults.insurance.amount || 0}% of freight)`}
                                      </span>
                                    )}
                                  </label>
                                  <input
                                    type="text"
                                    name="insurance_charges"
                                    value={formData.insurance_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditAdditionalCharges}
                                    className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${canEditAdditionalCharges ? "bg-white" : "bg-gray-100 cursor-not-allowed"}`}
                                  />
                                </div>
                              )}
                              {chargeDefaults.loading.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Loading
                                  </label>
                                  <input
                                    type="text"
                                    name="loading_charges"
                                    value={formData.loading_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditChargeField("loading_charges")}
                                    className={chargeFieldClass("loading_charges")}
                                  />
                                  {isFixedPerParcelCharge(chargeDefaults, 'loading') && toNum(formData.loading_charges) > 0 && (
                                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                                      ₹{toNum(formData.loading_charges)} × {parcelCount} = ₹{scaledPerParcelCharge(formData.loading_charges, parcelCount)}
                                    </p>
                                  )}
                                </div>
                              )}
                              {false && chargeDefaults.unloading.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Unloading
                                  </label>
                                  <input
                                    type="text"
                                    name="unloading_charges"
                                    value={formData.unloading_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditAdditionalCharges}
                                    className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${canEditAdditionalCharges ? "bg-white" : "bg-gray-100 cursor-not-allowed"}`}
                                  />
                                  {isFixedPerParcelCharge(chargeDefaults, 'unloading') && toNum(formData.unloading_charges) > 0 && (
                                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                                      ₹{toNum(formData.unloading_charges)} × {parcelCount} = ₹{scaledPerParcelCharge(formData.unloading_charges, parcelCount)}
                                    </p>
                                  )}
                                </div>
                              )}
                              {chargeDefaults.toll.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Toll
                                  </label>
                                  <input
                                    type="text"
                                    name="toll_charges"
                                    value={formData.toll_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditAdditionalCharges}
                                    className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${canEditAdditionalCharges ? "bg-white" : "bg-gray-100 cursor-not-allowed"}`}
                                  />
                                </div>
                              )}
                              {chargeDefaults.fuel_surcharge.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Fuel Surcharge
                                  </label>
                                  <input
                                    type="text"
                                    name="fuel_surcharge"
                                    value={formData.fuel_surcharge}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditAdditionalCharges}
                                    className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${canEditAdditionalCharges ? "bg-white" : "bg-gray-100 cursor-not-allowed"}`}
                                  />
                                </div>
                              )}
                              {chargeDefaults.packing.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Packing
                                  </label>
                                  <input
                                    type="text"
                                    name="packing_charges"
                                    value={formData.packing_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditAdditionalCharges}
                                    className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${canEditAdditionalCharges ? "bg-white" : "bg-gray-100 cursor-not-allowed"}`}
                                  />
                                </div>
                              )}
                              {chargeDefaults.handling?.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Handling Charges
                                  </label>
                                  <input
                                    type="text"
                                    name="handling_charges"
                                    value={formData.handling_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditAdditionalCharges}
                                    className={`px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 ${canEditAdditionalCharges ? "bg-white" : "bg-gray-100 cursor-not-allowed"}`}
                                  />
                                </div>
                              )}
                              {chargeDefaults.docket?.enabled && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Docket Fee
                                  </label>
                                  <input
                                    type="text"
                                    name="docket_fee"
                                    value={formData.docket_fee}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditChargeField("docket_fee")}
                                    className={chargeFieldClass("docket_fee")}
                                  />
                                </div>
                              )}
                              <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Door Pickup
                                  </label>
                                  <input
                                    type="text"
                                    name="door_pickup_charges"
                                    value={formData.door_pickup_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditChargeField("door_pickup_charges")}
                                    className={chargeFieldClass("door_pickup_charges")}
                                  />
                                  {isFixedPerParcelCharge(chargeDefaults, 'door_pickup') && toNum(formData.door_pickup_charges) > 0 && (
                                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                                      ₹{toNum(formData.door_pickup_charges)} × {parcelCount} = ₹{scaledPerParcelCharge(formData.door_pickup_charges, parcelCount)}
                                    </p>
                                  )}
                                </div>
                              <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Door Delivery
                                  </label>
                                  <input
                                    type="text"
                                    name="delivery_charges"
                                    value={formData.delivery_charges}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    disabled={!canEditChargeField("delivery_charges")}
                                    className={chargeFieldClass("delivery_charges")}
                                  />
                                  {isFixedPerParcelCharge(chargeDefaults, 'door_delivery') && toNum(formData.delivery_charges) > 0 && (
                                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                                      ₹{toNum(formData.delivery_charges)} × {parcelCount} = ₹{scaledPerParcelCharge(formData.delivery_charges, parcelCount)}
                                    </p>
                                  )}
                                </div>
                              <div className="flex flex-col">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Other Charges
                                </label>
                                <input
                                  type="text"
                                  name="other_charges"
                                  value={formData.other_charges}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200 bg-white"
                                />
                              </div>
                            </div>
                          )}

                          <div className="space-y-0">
                            {/* Primary charges — Freight, Rate/KG, Discount */}
                            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-3 mb-3">
                              <div className="flex flex-col">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Freight Charges
                                </label>
                                <input
                                  type="text"
                                  value={calculateFreightCharges()}
                                  onKeyDown={handleEnterAsTab}
                                  disabled
                                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 text-gray-600 font-semibold cursor-not-allowed"
                                />
                              </div>

                              {(isCargoMode || busFreightByWeight) && (
                                <div className="flex flex-col">
                                  <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                    Rate / KG
                                  </label>
                                  <input
                                    type="text"
                                    name="rate_per_kg"
                                    value={formData.rate_per_kg}
                                    onChange={handleChange}
                                    onKeyDown={handleEnterAsTab}
                                    placeholder="From receiver hub (optional)"
                                    className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                                  />
                                </div>
                              )}

                              <div className="flex flex-col">
                                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                  Discount
                                </label>
                                <input
                                  type="text"
                                  name="discount"
                                  value={formData.discount}
                                  onChange={handleChange}
                                  onKeyDown={handleEnterAsTab}
                                  className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                                />
                              </div>
                            </div>

                          </div>
                          <div className="mt-4 max-w-xs">
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                              Total Amount
                            </label>
                            <div className="px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl bg-gray-100 font-semibold text-green-600">
                              ₹{calculateTotalAmount()}
                            </div>
                            {useShipmentClient && (
                              <p className="mt-1.5 text-[11px] text-gray-500">
                                Credit Clients are billed monthly — payment status is set to Credit.
                              </p>
                            )}
                          </div>

                          {(formData.mode_of_payment === "paid" ||
                            formData.mode_of_payment === "paid_balance" ||
                            (formData.mode_of_payment !== "to_pay" && formData.mode_of_payment !== "credit")) && (
                            <div ref={paidPaymentFieldsRef} className="space-y-4 mt-4 pb-2 overflow-visible">
                              <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                                {formData.mode_of_payment !== "to_pay" && formData.mode_of_payment !== "credit" && (
                                  <div className="flex flex-col">
                                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                      Payment Mode
                                    </label>
                                    <div className={`grid grid-cols-2 gap-1.5 ${!formData.payment_mode ? "ring-2 ring-red-200 rounded-xl p-0.5" : ""}`}>
                                      {[{ value: "cash", label: "Cash" }, { value: "upi", label: "UPI" }].map((option) => {
                                        const selected = formData.payment_mode === option.value
                                        return (
                                          <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => handleChange({ target: { name: "payment_mode", value: option.value } })}
                                            className={`min-w-0 px-2 py-2 sm:py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition ${
                                              selected
                                                ? "bg-blue-50 border-blue-500 text-blue-800 shadow-sm"
                                                : "bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/40"
                                            }`}
                                          >
                                            {option.label}
                                          </button>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )}

                                {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                                  <div className="flex flex-col">
                                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                                      Paid Amount
                                    </label>
                                    <input
                                      type="text"
                                      name="paid_amount"
                                      placeholder="0.00"
                                      value={formData.paid_amount}
                                      onChange={handleChange}
                                      onKeyDown={handleEnterAsTab}
                                      className="px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-orange-200"
                                    />
                                  </div>
                                )}
                              </div>

                              {(formData.mode_of_payment === "paid" || formData.mode_of_payment === "paid_balance") && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl">
                                    <p className="text-xs text-gray-600 font-semibold">Paid Amount</p>
                                    <p className="text-lg font-bold text-blue-600">₹{formData.paid_amount || "0.00"}</p>
                                  </div>
                                  <div className="bg-blue-50 p-3 rounded-xl">
                                    <p className="text-xs text-gray-600 font-semibold">Balance Due</p>
                                    <p className="text-lg font-bold text-blue-600">₹{calculateBalanceAmount()}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                    </div>
                  </div>

                </div>
                </div>
                </form>

                {isMobileLayout && (
                  <div
                    className={`fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden ${
                      showCreatePreview ? "pointer-events-none invisible" : ""
                    }`}
                  >
                    {mobileStep > 0 ? (
                      <Button variant="secondary" type="button" icon={ChevronLeft} onClick={goToPrevMobileStep}>
                        Back
                      </Button>
                    ) : (
                      <div className="w-16" />
                    )}
                    <Button
                      variant="primary"
                      type="button"
                      icon={mobileStep < MOBILE_STEPS.length - 1 ? ChevronRight : undefined}
                      iconPosition="right"
                      onClick={handleMobilePrimaryAction}
                      loading={loading && mobileStep >= MOBILE_STEPS.length - 1}
                      disabled={loading || showCreatePreview || (mobileStep >= MOBILE_STEPS.length - 1 && autoMlOverweight)}
                    >
                      {mobileStep < MOBILE_STEPS.length - 1
                        ? "Next"
                        : loading
                          ? "Creating..."
                          : "Create Shipment"}
                    </Button>
                  </div>
                )}

      {showEwayBillModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-xl shadow-lg w-[95%] max-w-md">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-blue-600 text-white rounded-t-sm">
        <h3 className="text-sm font-semibold">Add E-WayBill</h3>
        <Button variant="ghost" onClick={handleCloseEwayBillModal} className="text-white hover:bg-white/20" disabled={ewayLookupLoading}>✕</Button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {ewaybillFeatureOn && (
          <p className="text-xs text-gray-500">
            Enter the 12-digit E-way bill number. Details will be fetched and filled on this page.
          </p>
        )}

        {ewayBills.map((value, index) => {
          const isValid = isValidEway(value)

          return (
            <div key={index}>
              <input
                type="text"
                value={value}
                maxLength={12}
                inputMode="numeric"
                placeholder="Enter 12-digit E-Way Bill No."
                disabled={ewayLookupLoading}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "")
                  const updated = [...ewayBills]
                  updated[index] = val
                  setEwayBills(updated)
                }}
                className={`w-full px-3 py-2 border rounded-xl
                  ${value && !isValid ? "border-red-500" : "border-gray-300"}
                  focus:outline-none`}
              />

              {value && !isValid && (
                <p className="text-xs text-red-500 mt-1">
                  E-Way Bill must be exactly 12 digits
                </p>
              )}
            </div>
          )
        })}

        {/* Buttons */}
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" size="sm" disabled={ewayLookupLoading || !isValidEway(ewayBills[ewayBills.length - 1])} onClick={() => setEwayBills([...ewayBills, ""])}>
            Add New
          </Button>

          <Button variant="danger" size="sm" disabled={ewayLookupLoading || ewayBills.length === 1} onClick={() => setEwayBills(ewayBills.slice(0, -1))}>
            Remove Last
          </Button>

          <Button variant="success" size="sm" loading={ewayLookupLoading} disabled={ewayLookupLoading || !ewayBills.some(isValidEway)} onClick={handleSubmitEwayBills}>
            {ewaybillFeatureOn ? (ewayLookupLoading ? "Fetching..." : "Fetch & Add") : "Submit"}
          </Button>
        </div>
      </div>

      {/* Footer */}
            <div className="flex justify-end px-4 py-2 border-t">
              <Button variant="secondary" size="sm" onClick={handleCloseEwayBillModal} disabled={ewayLookupLoading}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      {showViewEwayBillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg w-[95%] max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-green-600 text-white rounded-t-sm">
              <h3 className="text-sm font-semibold">Submitted E-Way Bills ({submittedEwayBills.length})</h3>
              <Button variant="ghost" onClick={() => setShowViewEwayBillModal(false)} className="text-white hover:bg-white/20">✕</Button>
            </div>

            {/* Body */}
            <div className="p-4">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {submittedEwayBills.map((bill, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-green-600 text-white text-xs font-bold rounded-full">
                        {index + 1}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">{bill}</span>
                    </div>
                    <Button variant="danger" size="sm" onClick={() => handleRemoveEwayBill(index)}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between px-4 py-2 border-t">
              <Button variant="primary" size="sm" onClick={() => {
                  setShowViewEwayBillModal(false)
                  setShowEwayBillModal(true)
                }}>
                Add More
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowViewEwayBillModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      <ShipmentCreatePreviewModal
        open={showCreatePreview}
        onClose={() => !loading && setShowCreatePreview(false)}
        onConfirm={() => handleSubmit()}
        loading={loading}
        formData={formData}
        senderLocationDisplay={senderLocationDisplay}
        receiverLocationDisplay={receiverLocationDisplay}
        senderHubContact={
          consignerLocations.find((h) => String(h.id) === String(senderHubId || formData.sender_location_hub_id))
            ?.location_hub_contact || ""
        }
        receiverHubContact={
          consigneeLocations.find((h) => String(h.id) === String(receiverHubId || formData.receiver_location_hub_id))
            ?.location_hub_contact || ""
        }
        parcels={parcels}
        onQtyChange={handleConfirmModalQtyChange}
        isCargoMode={isCargoMode}
        busFreightByWeight={busFreightByWeight}
        freightCharges={calculateFreightCharges()}
        totalAmount={calculateTotalAmount()}
        balanceAmount={calculateBalanceAmount()}
        gstAmounts={calculateGSTAmounts()}
        paymentStatusLabel={
          paymentStatusOptions.find((option) => option.value === formData.mode_of_payment)?.label
          || formData.mode_of_payment
        }
        gcPreview={
          locationCode
            ? `${locationCode}-${gcPayCode}-${gcYearSuffix}-{seq}`
            : "Auto-generated on submit"
        }
        submittedEwayBills={submittedEwayBills}
        autoMl={
          showAutoMlSection && autoMlEnabled
            ? {
                enabled: true,
                mode: autoMlMode,
                vehicleLabel: autoMlSelectedVehicle?.vehicle_number || "",
                agentLabel: (() => {
                  const agent = autoMlOptions.agents.find((item) => item.user_id === autoMlAgentUserId)
                  return agent ? `${agent.name}${agent.city ? ` - ${agent.city}` : ""}` : ""
                })(),
                mlSheetId: autoMlMode === "existing" ? selectedExistingMlId : "",
              }
            : null
        }
        chargeDefaults={chargeDefaults}
        mediaFiles={shipmentMediaFiles}
        selectedClient={selectedClient}
        useShipmentClient={useShipmentClient}
      />
      <PaymentQrModal
        open={showPaymentQr}
        intent={paymentIntent}
        title="Scan UPI QR to Create Shipment"
        onSuccess={handleOnlinePaymentSuccess}
        onCancel={handleOnlinePaymentCancel}
      />
      {!isMobileLayout && (
        <Button
          variant="primary"
          type="button"
          icon={Send}
          onClick={handleFormSubmit}
          loading={loading}
          disabled={loading || autoMlOverweight || showCreatePreview}
          className="hidden md:inline-flex fixed bottom-6 right-6 z-40 shadow-xl !rounded-full px-5 py-3"
        >
          {loading ? "Creating..." : "Create Shipment"}
        </Button>
      )}
    </DashboardLayout>
  )
}

export default AddShipment;